<template>
  <div class="ai-calling-tab">
    <div v-if="!canView" class="ai-empty-panel">
      <feather-icon icon="LockIcon" size="28" class="ai-empty-panel__icon" />
      <p class="ai-empty-panel__title">No access to AI Calling</p>
      <p class="ai-empty-panel__hint mb-0">
        You do not have permission to use AI Calling on this job. Contact an administrator if you need access.
      </p>
    </div>

    <template v-else>
      <header class="ai-page-header">
        <p class="ai-page-subtitle">{{ flowPhaseSubtitle }}</p>
      </header>

      <b-alert v-if="configWarning" variant="warning" show class="ai-banner mb-1">
        <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 flex-shrink-0" />
        <span>{{ configWarning }}</span>
      </b-alert>

      <b-alert v-if="sessionBlocked" variant="info" show class="ai-banner mb-1">
        <feather-icon icon="UserIcon" size="16" class="mr-50 flex-shrink-0" />
        <span>
          <strong>{{ activeSessionUserName }}</strong> is using AI Calling on this job. Calling is read-only until they leave.
        </span>
      </b-alert>

      <div class="ai-question-flow">
        <template v-if="flowPhase === 'prompt'">
          <div class="ai-setup-grid">
          <div class="ai-question-form ai-section-card">
            <ai-calling-call-type-picker
              :call-type="callType"
              :hint="callTypePickerHint"
              :disabled="sessionBlocked || !canEditPrompt"
              @update:callType="onCallTypeChange"
            >
              <div
                v-if="showAgentStep"
                class="ai-form-block"
              >
                <ai-calling-agent-picker
                  :agents="pickerAgents"
                  :selected-agent-id="selectedAgentId"
                  :typed-agent-id="typedAgentId"
                  :org-assigned-id="orgAssignedAgentId"
                  :job-agent-id="jobAgentId"
                  :default-agent-name="adminAgentName"
                  :call-type="callType"
                  :loading="agentsLoading"
                  :empty-message="agentsEmptyMessage"
                  :allow-retry="showAgentListRetry"
                  :show-typed-fallback="showTypedAgentFallback"
                  :disabled="sessionBlocked || !canUseJobCalling"
                  :hint="agentPickerHint"
                  @select="onAgentSelected"
                  @typed="onTypedAgentId"
                  @retry="loadAvailableAgents"
                />
                <p
                  v-if="hasSelectedAgent && selectedAgentVoiceLine"
                  class="text-muted small mb-50 mt-25"
                >
                  {{ selectedAgentVoiceLine }}
                </p>
                <p
                  v-if="brokenHindiConfigDial"
                  class="text-warning small mb-50 mt-25"
                >
                  {{ brokenHindiConfigHint }}
                </p>
                <div
                  v-if="hasSelectedAgent && agentSetupStatus"
                  class="ai-agent-setup"
                  :class="`ai-agent-setup--${agentReadyDisplayStatus}`"
                  role="status"
                  aria-live="polite"
                >
                  <template v-if="agentSetupStatus === 'setting_up' || (agentSetupStatus === 'ready' && generatingPrompt)">
                    <b-spinner small class="ai-agent-setup__icon flex-shrink-0" />
                    <span>{{ generatingPrompt ? 'Updating the script…' : 'Getting the agent ready…' }}</span>
                  </template>
                  <template v-else-if="agentSetupStatus === 'ready' && !scriptValidationOk">
                    <feather-icon icon="AlertCircleIcon" size="18" class="ai-agent-setup__icon flex-shrink-0" />
                    <span>{{ selectedAgentReadyLabel }}</span>
                  </template>
                  <template v-else-if="agentSetupStatus === 'ready'">
                    <feather-icon icon="CheckCircleIcon" size="18" class="ai-agent-setup__icon flex-shrink-0" />
                    <span>{{ selectedAgentReadyLabel }}</span>
                  </template>
                  <template v-else-if="agentSetupStatus === 'error'">
                    <feather-icon icon="AlertCircleIcon" size="18" class="ai-agent-setup__icon flex-shrink-0" />
                    <div class="ai-agent-setup__body">
                      <p class="mb-50">{{ agentSetupError || 'Could not set up this agent. Try again.' }}</p>
                      <b-button
                        variant="outline-danger"
                        :disabled="sessionBlocked || !canUseJobCalling"
                        @click="retryVoiceAgentSetup"
                      >
                        Retry
                      </b-button>
                    </div>
                  </template>
                </div>
              </div>

              <div
                v-if="showScriptStep"
                class="ai-form-block"
              >
                <ai-calling-simple-prompt-picker
                  :call-type="callType"
                  :selected-preset-id.sync="selectedPresetId"
                  :custom-brief.sync="customPromptBrief"
                  :other-brief.sync="otherCallLabel"
                  :preview-opening="promptPreviewOpening"
                  :language-hint="transcriptLanguageLabel"
                  :generating="generatingPrompt"
                  :disabled="sessionBlocked || !canEditPrompt"
                  :show-preview="false"
                  optional
                  @change="onPromptChange"
                />
                <div
                  v-if="hasCallType && hasSelectedAgent && isVoiceAgentReady"
                  class="ai-prompt-job-details"
                >
                  <b-button
                    variant="link"
                    class="ai-prompt-job-details__toggle w-100 text-left"
                    :disabled="sessionBlocked || !canEditPrompt"
                    @click="promptJobDetailsOpen = !promptJobDetailsOpen"
                  >
                    <feather-icon
                      :icon="promptJobDetailsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                      size="16"
                      class="mr-50 flex-shrink-0"
                    />
                    <span class="mb-0">{{ promptJobDetailsOpen ? 'Hide job details' : 'Tweak job details' }}</span>
                    <b-badge variant="light-secondary" class="ml-50 ai-optional-badge align-middle">Optional</b-badge>
                  </b-button>
                  <b-collapse v-model="promptJobDetailsOpen">
                    <div class="ai-prompt-job-details__body">
                      <ai-calling-variable-editor
                        v-model="editableVars"
                        :show-drive-fields="callType === 'drive'"
                        :disabled="sessionBlocked || !canEditPrompt"
                        :show-label="false"
                        compact
                      />
                    </div>
                  </b-collapse>
                </div>
              </div>

            </ai-calling-call-type-picker>
          </div>

          <aside class="ai-setup-aside" aria-label="Preview and test call">
            <section class="ai-section-card ai-aside-card">
              <div class="ai-section-card__header">
                <span class="ai-section-card__icon" aria-hidden="true">
                  <feather-icon icon="CheckCircleIcon" size="16" />
                </span>
                <div>
                  <h3 class="ai-section-card__title">Progress</h3>
                  <p class="ai-section-card__desc">{{ setupAsideStatus }}</p>
                </div>
              </div>
              <div class="ai-section-card__body">
                <ol class="ai-setup-steps ai-setup-steps--aside" aria-label="Setup progress">
                  <li
                    v-for="(step, index) in setupSteps"
                    :key="step.id"
                    class="ai-setup-steps__item"
                    :class="{
                      'ai-setup-steps__item--done': step.done,
                      'ai-setup-steps__item--current': step.current,
                    }"
                  >
                    <span class="ai-setup-steps__index" aria-hidden="true">
                      <feather-icon v-if="step.done" icon="CheckIcon" size="12" />
                      <template v-else>{{ index + 1 }}</template>
                    </span>
                    <span class="ai-setup-steps__label">{{ step.label }}</span>
                  </li>
                </ol>
              </div>
            </section>

            <section
              v-if="generatingPrompt || promptPreviewOpening"
              class="ai-section-card ai-aside-card"
              aria-labelledby="ai-aside-preview-heading"
            >
              <div class="ai-section-card__header">
                <span class="ai-section-card__icon" aria-hidden="true">
                  <feather-icon icon="MessageCircleIcon" size="16" />
                </span>
                <div class="flex-grow-1">
                  <h3 id="ai-aside-preview-heading" class="ai-section-card__title">Here’s how it opens</h3>
                  <p class="ai-section-card__desc mb-0">
                    {{ generatingPrompt ? 'Updating the script…' : 'This is the first line the candidate will hear.' }}
                  </p>
                </div>
                <b-badge v-if="transcriptLanguageLabel && !generatingPrompt" variant="light-primary" class="ai-language-badge">
                  {{ transcriptLanguageLabel }}
                </b-badge>
              </div>
              <div class="ai-section-card__body">
                <div v-if="generatingPrompt" class="ai-generating-hint mb-0">
                  <b-spinner small class="mr-50" />
                  Updating the script…
                </div>
                <blockquote v-else class="ai-aside-preview__quote mb-0">
                  {{ promptPreviewOpening }}
                </blockquote>
              </div>
            </section>

            <section
              class="ai-section-card ai-section-card--accent ai-aside-card"
              aria-labelledby="ai-aside-test-heading"
            >
              <div class="ai-section-card__header">
                <span class="ai-section-card__icon" aria-hidden="true">
                  <feather-icon icon="PhoneIcon" size="16" />
                </span>
                <div>
                  <h3 id="ai-aside-test-heading" class="ai-section-card__title">Test the call</h3>
                  <p class="ai-section-card__desc">
                    {{
                      isStep1Complete
                        ? 'We’ll call you with this script before anyone else is dialed.'
                        : 'Unlocks after the agent and script are ready.'
                    }}
                  </p>
                </div>
              </div>
              <div class="ai-section-card__body">
                <template v-if="isStep1Complete && canUseJobCalling">
                  <div class="ai-test-call-card">
                    <div class="ai-test-call-row">
                      <div class="ai-test-call-field">
                        <label class="ai-test-call-label" for="ai-test-phone-inline">Your phone number</label>
                        <b-form-input
                          id="ai-test-phone-inline"
                          v-model="testCallCandidatePhone"
                          type="tel"
                          maxlength="15"
                          placeholder="e.g. 9876543210"
                          autocomplete="tel"
                          :state="testCallPhoneFieldState"
                          :disabled="sessionBlocked || !canUseJobCalling || preparingStep || testingCall"
                          @keydown.enter.prevent="onTestCallEnter"
                        />
                        <b-form-invalid-feedback>
                          {{ testCallPhoneHelp }}
                        </b-form-invalid-feedback>
                        <p v-if="testCallPhoneFieldState !== false" class="ai-test-call-help mb-0">
                          {{ testCallPhoneHelp }}
                        </p>
                      </div>
                      <div class="ai-test-call-actions">
                        <b-button
                          variant="primary"
                          class="ai-cta"
                          :disabled="!canPlaceTestCall"
                          :title="testCallButtonTitle"
                          @click="handleTestCall"
                        >
                          <b-spinner v-if="preparingStep || testingCall" small class="mr-50" />
                          <feather-icon v-else icon="PhoneIcon" size="16" class="mr-50" />
                          Place test call
                        </b-button>
                      </div>
                    </div>
                  </div>
                  <p class="ai-next-step-hint mb-0">
                    After you hear the script, you can call candidates.
                  </p>
                </template>
                <p v-else class="ai-aside-placeholder mb-0">
                  Finish the questions on the left. The test call stays here so you can hear the script first.
                </p>
              </div>
            </section>
          </aside>
          </div>
        </template>

        <section
          v-else-if="flowPhase === 'approve'"
          class="ai-section-card ai-section-card--accent"
          aria-labelledby="ai-approve-heading"
        >
          <div class="ai-approval-gate">
            <div class="ai-approval-gate__icon-wrap">
              <feather-icon icon="PhoneCallIcon" size="26" />
            </div>
            <h3 id="ai-approve-heading" class="ai-approval-gate__title mb-25">Did the call sound right?</h3>
            <p class="ai-approval-gate__hint mb-1">
              Next: call candidates. You can still edit the script first.
            </p>
            <div class="d-flex flex-wrap justify-content-center ai-approval-gate__actions">
              <b-button
                variant="primary"
                class="mr-50 mb-50 ai-cta"
                :disabled="sessionBlocked || !canEditPrompt"
                @click="approveCall"
              >
                <feather-icon icon="PhoneForwardedIcon" size="16" class="mr-50" />
                Call candidates
              </b-button>
              <b-button
                variant="outline-secondary"
                class="mb-50 ai-cta-secondary"
                :disabled="sessionBlocked || !canEditPrompt"
                @click="rejectCall"
              >
                <feather-icon icon="EditIcon" size="16" class="mr-50" />
                Edit script
              </b-button>
            </div>
          </div>
        </section>

        <template v-else-if="flowPhase === 'edit'">
          <div class="ai-editor-layout">
          <section class="ai-section-card ai-editor-layout__details" aria-labelledby="ai-edit-details-heading">
            <button
              type="button"
              class="ai-section-card__header ai-details-accordion__toggle"
              :aria-expanded="editJobDetailsOpen ? 'true' : 'false'"
              aria-controls="ai-edit-details-body"
              @click="editJobDetailsOpen = !editJobDetailsOpen"
            >
              <span class="ai-section-card__icon" aria-hidden="true">
                <feather-icon icon="BriefcaseIcon" size="16" />
              </span>
              <div class="flex-grow-1 min-width-0 text-left">
                <h3 id="ai-edit-details-heading" class="ai-section-card__title">Job details</h3>
                <p v-if="editJobDetailsOpen" class="ai-section-card__desc">
                  These names fill the teal chips in the script.
                </p>
                <p v-else class="ai-details-accordion__summary mb-0">
                  {{ jobDetailsSummary }}
                </p>
              </div>
              <feather-icon
                :icon="editJobDetailsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="18"
                class="ai-details-accordion__chevron flex-shrink-0"
              />
            </button>
            <b-collapse id="ai-edit-details-body" v-model="editJobDetailsOpen">
              <div class="ai-section-card__body">
                <ai-calling-variable-editor
                  v-model="editableVars"
                  :show-drive-fields="callType === 'drive'"
                  :disabled="sessionBlocked || !canEditPrompt"
                  :show-label="false"
                  show-token-hint
                  compact
                />
              </div>
            </b-collapse>
          </section>

          <section class="ai-section-card ai-editor-layout__script" aria-labelledby="ai-edit-script-heading">
            <button
              type="button"
              class="ai-section-card__header ai-details-accordion__toggle"
              :aria-expanded="editScriptOpen ? 'true' : 'false'"
              aria-controls="ai-edit-script-body"
              @click="editScriptOpen = !editScriptOpen"
            >
              <span class="ai-section-card__icon" aria-hidden="true">
                <feather-icon icon="MessageCircleIcon" size="16" />
              </span>
              <div class="flex-grow-1 min-width-0 text-left">
                <h3 id="ai-edit-script-heading" class="ai-section-card__title">Script</h3>
                <p v-if="editScriptOpen" class="ai-section-card__desc">
                  Edit a step, then hear a demo. Chips are values from job details.
                </p>
                <p v-else class="ai-details-accordion__summary mb-0">
                  {{ scriptAccordionSummary }}
                </p>
              </div>
              <b-badge variant="light-primary" class="ai-script-count-badge">
                {{ scriptFlowSteps.length }} step{{ scriptFlowSteps.length === 1 ? '' : 's' }}
              </b-badge>
              <feather-icon
                :icon="editScriptOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="18"
                class="ai-details-accordion__chevron flex-shrink-0"
              />
            </button>
            <b-collapse id="ai-edit-script-body" v-model="editScriptOpen">
              <div class="ai-section-card__body">
                <ai-calling-script-flow
                  :steps="scriptFlowSteps"
                  editable
                  :disabled="sessionBlocked || !canEditPrompt"
                  :previewing-step-id="previewingStepId"
                  :variable-values="jobPromptValues"
                  @update-step="onScriptFlowStepEdit"
                  @demo-step="previewFlowStep"
                />
                <audio
                  v-if="previewUrl"
                  ref="previewAudio"
                  class="ai-preview-audio w-100 mt-1"
                  controls
                  :src="previewUrl"
                />
              </div>
            </b-collapse>
          </section>
          </div>

          <section
            v-if="canEditPrompt"
            class="ai-section-card ai-section-card--accent"
            aria-labelledby="ai-edit-test-heading"
          >
            <div class="ai-section-card__header">
              <span class="ai-section-card__icon" aria-hidden="true">
                <feather-icon icon="PhoneIcon" size="16" />
              </span>
              <div>
                <h3 id="ai-edit-test-heading" class="ai-section-card__title">Test call</h3>
                <p class="ai-section-card__desc">Hear the updated script, or finish editing and continue.</p>
              </div>
            </div>
            <div class="ai-section-card__body">
              <div class="ai-test-call-card">
                <div class="ai-test-call-row">
                  <div class="ai-test-call-field">
                    <label class="ai-test-call-label" for="ai-test-phone-edit">Your phone number</label>
                    <b-form-input
                      id="ai-test-phone-edit"
                      v-model="testCallCandidatePhone"
                      type="tel"
                      maxlength="15"
                      placeholder="e.g. 9876543210"
                      autocomplete="tel"
                      :state="testCallPhoneFieldState"
                      :disabled="sessionBlocked || preparingStep || testingCall"
                      @keydown.enter.prevent="onTestCallEnter"
                    />
                    <b-form-invalid-feedback>
                      {{ testCallPhoneHelp }}
                    </b-form-invalid-feedback>
                    <p v-if="testCallPhoneFieldState !== false" class="ai-test-call-help mb-0">
                      {{ testCallPhoneHelp }}
                    </p>
                  </div>
                  <div class="ai-test-call-actions ai-test-call-actions--edit">
                    <b-button
                      variant="outline-secondary"
                      class="ai-cta-secondary"
                      :disabled="sessionBlocked"
                      @click="finishEditing"
                    >
                      <feather-icon icon="CheckIcon" size="16" class="mr-50" />
                      Done editing
                    </b-button>
                    <b-button
                      variant="primary"
                      class="ai-cta"
                      :disabled="!canPlaceTestCall"
                      :title="testCallButtonTitle"
                      @click="handleTestCall"
                    >
                      <b-spinner v-if="preparingStep || testingCall" small class="mr-50" />
                      <feather-icon v-else icon="PhoneIcon" size="16" class="mr-50" />
                      Place test call
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </template>

        <template v-else-if="flowPhase === 'configure'">
          <div class="ai-editor-layout">
          <section class="ai-section-card ai-editor-layout__details" aria-labelledby="ai-cfg-details-heading">
            <button
              type="button"
              class="ai-section-card__header ai-details-accordion__toggle"
              :aria-expanded="editJobDetailsOpen ? 'true' : 'false'"
              aria-controls="ai-cfg-details-body"
              @click="editJobDetailsOpen = !editJobDetailsOpen"
            >
              <span class="ai-section-card__icon" aria-hidden="true">
                <feather-icon icon="BriefcaseIcon" size="16" />
              </span>
              <div class="flex-grow-1 min-width-0 text-left">
                <h3 id="ai-cfg-details-heading" class="ai-section-card__title">Job details</h3>
                <p v-if="editJobDetailsOpen" class="ai-section-card__desc">
                  Confirm the names the agent will say.
                </p>
                <p v-else class="ai-details-accordion__summary mb-0">
                  {{ jobDetailsSummary }}
                </p>
              </div>
              <feather-icon
                :icon="editJobDetailsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="18"
                class="ai-details-accordion__chevron flex-shrink-0"
              />
            </button>
            <b-collapse id="ai-cfg-details-body" v-model="editJobDetailsOpen">
              <div class="ai-section-card__body">
                <ai-calling-variable-editor
                  v-model="editableVars"
                  :show-drive-fields="callType === 'drive'"
                  :disabled="sessionBlocked || !canEditPrompt"
                  :show-label="false"
                  show-token-hint
                  compact
                />
              </div>
            </b-collapse>
          </section>

          <section class="ai-section-card ai-editor-layout__script" aria-labelledby="ai-cfg-script-heading">
            <button
              type="button"
              class="ai-section-card__header ai-details-accordion__toggle"
              :aria-expanded="editScriptOpen ? 'true' : 'false'"
              aria-controls="ai-cfg-script-body"
              @click="editScriptOpen = !editScriptOpen"
            >
              <span class="ai-section-card__icon" aria-hidden="true">
                <feather-icon icon="MessageCircleIcon" size="16" />
              </span>
              <div class="flex-grow-1 min-width-0 text-left">
                <h3 id="ai-cfg-script-heading" class="ai-section-card__title">Script</h3>
                <p v-if="editScriptOpen" class="ai-section-card__desc">
                  Review the call flow. Chips are values from job details.
                </p>
                <p v-else class="ai-details-accordion__summary mb-0">
                  {{ scriptAccordionSummary }}
                </p>
              </div>
              <b-badge variant="light-primary" class="ai-script-count-badge">
                {{ scriptFlowSteps.length }} step{{ scriptFlowSteps.length === 1 ? '' : 's' }}
              </b-badge>
              <feather-icon
                :icon="editScriptOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="18"
                class="ai-details-accordion__chevron flex-shrink-0"
              />
            </button>
            <b-collapse id="ai-cfg-script-body" v-model="editScriptOpen">
              <div class="ai-section-card__body">
                <ai-calling-script-flow
                  :steps="scriptFlowSteps"
                  :disabled="sessionBlocked || !canEditPrompt"
                  :previewing-step-id="previewingStepId"
                  :variable-values="jobPromptValues"
                  @demo-step="previewFlowStep"
                />
                <audio
                  v-if="previewUrl"
                  ref="previewAudio"
                  class="ai-preview-audio w-100 mt-1"
                  controls
                  :src="previewUrl"
                />
              </div>
            </b-collapse>
          </section>
          </div>

          <section class="ai-section-card ai-section-card--accent" aria-labelledby="ai-candidates-heading">
            <div class="ai-section-card__header">
              <span class="ai-section-card__icon" aria-hidden="true">
                <feather-icon icon="UsersIcon" size="16" />
              </span>
              <div>
                <h3 id="ai-candidates-heading" class="ai-section-card__title">Candidates</h3>
                <p class="ai-section-card__desc">Choose who to include, then start calling.</p>
              </div>
            </div>
            <div class="ai-section-card__body">
              <div class="d-flex flex-wrap align-items-center mb-1 ai-calling-actions__toolbar">
                <b-button
                  v-if="canRun"
                  variant="primary"
                  class="mr-50 mb-50 ai-cta"
                  :disabled="!canStart || starting"
                  @click="confirmStart"
                >
                  <b-spinner v-if="starting" small class="mr-50" />
                  <feather-icon v-else icon="PhoneForwardedIcon" size="16" class="mr-50" />
                  Start calling
                </b-button>
                <b-button
                  v-if="canEditPrompt"
                  variant="outline-secondary"
                  class="mb-50 ai-cta-secondary"
                  :disabled="sessionBlocked"
                  @click="backToPromptPhase"
                >
                  <feather-icon icon="Edit2Icon" size="16" class="mr-50" />
                  Edit script
                </b-button>
              </div>

              <b-form-group label="Call candidates in these statuses" label-class="ai-field-label">
                <v-select
                  :value="selectedStatuses"
                  :options="statusOptions"
                  label="title"
                  :reduce="option => option.title"
                  multiple
                  :close-on-select="false"
                  :disabled="sessionBlocked || !canRun"
                  placeholder="Select statuses…"
                  @input="onStatusesChange"
                />
              </b-form-group>
              <div class="ai-status-line mb-1">
                <feather-icon icon="UsersIcon" size="16" class="mr-50 flex-shrink-0" />
                <span>{{ eligibleCount }} candidate{{ eligibleCount === 1 ? '' : 's' }} ready · {{ linesStatusLabel }}</span>
              </div>

              <div v-if="activeCampaign" class="ai-calling-campaign">
                <div class="d-flex flex-wrap justify-content-between align-items-center mb-75">
                  <span class="ai-campaign-title mb-0">Campaign progress</span>
                  <div v-if="canRun && isCampaignActive">
                    <b-button
                      variant="outline-warning"
                      class="mr-50 ai-cta-secondary"
                      :disabled="acting"
                      @click="pauseCampaign"
                    >
                      Pause
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      class="ai-cta-secondary"
                      :disabled="acting"
                      @click="cancelCampaign"
                    >
                      Cancel
                    </b-button>
                  </div>
                </div>

                <div class="d-flex flex-wrap ai-campaign-pills mb-75">
                  <b-badge class="ai-status-pill mr-50 mb-25" variant="light-secondary">Queued {{ progress.queued }}</b-badge>
                  <b-badge class="ai-status-pill mr-50 mb-25" variant="light-info">Calling {{ progress.calling }}</b-badge>
                  <b-badge class="ai-status-pill mr-50 mb-25" variant="light-success">Done {{ progress.completed }}</b-badge>
                  <b-badge class="ai-status-pill mr-50 mb-25" variant="light-danger">Failed {{ progress.failed }}</b-badge>
                  <b-badge class="ai-status-pill mb-25" :variant="campaignStatusVariant">{{ campaignStatusLabel }}</b-badge>
                </div>

                <b-progress
                  :max="progressTotal || 1"
                  height="0.6rem"
                  class="mb-50"
                  show-progress
                >
                  <b-progress-bar :value="progress.completed" variant="success" />
                  <b-progress-bar :value="progress.failed" variant="danger" />
                  <b-progress-bar :value="progress.calling" variant="info" />
                </b-progress>

                <p class="ai-campaign-meta mb-0">
                  {{ callTypeLabel }} · Campaign {{ activeCampaign.id || activeCampaign.campaignId }}
                </p>
              </div>
            </div>
          </section>
        </template>
      </div>

      <section v-if="canView && hasCallType" class="ai-section-card ai-history-section" aria-labelledby="ai-history-heading">
        <b-button
          variant="link"
          class="ai-section-card__header ai-history-toggle w-100 text-left"
          @click="callHistoryOpen = !callHistoryOpen"
        >
          <span class="ai-section-card__icon" aria-hidden="true">
            <feather-icon icon="ClockIcon" size="16" />
          </span>
          <div class="flex-grow-1">
            <h3 id="ai-history-heading" class="ai-section-card__title">History</h3>
            <p class="ai-section-card__desc mb-0">
              {{ callHistoryOpen ? 'Calls placed for this job.' : 'Show time, candidate, status, and duration.' }}
            </p>
          </div>
          <feather-icon
            :icon="callHistoryOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
            size="18"
            class="flex-shrink-0"
          />
        </b-button>
        <b-collapse v-model="callHistoryOpen">
          <div class="ai-section-card__body ai-section-card__body--top-border">
            <ai-calling-call-history
              v-if="callHistoryOpen"
              ref="callHistory"
              :job-id="jobId"
              :candidates="candidates"
            />
          </div>
        </b-collapse>
      </section>

      <b-modal
        id="ai-calling-confirm"
        v-model="showConfirm"
        title="Start calling candidates?"
        ok-title="Start calling"
        cancel-title="Cancel"
        @ok="startCampaign"
      >
        <p class="mb-50">
          You are about to call <strong>{{ eligibleCount }}</strong> candidate{{ eligibleCount === 1 ? '' : 's' }}
          as a <strong>{{ callTypeLabel }}</strong>.
        </p>
        <p class="text-muted mb-0 small">
          Up to 2 calls run at a time on shared company lines.
        </p>
      </b-modal>

      <b-modal
        id="ai-calling-test-call"
        v-model="showTestCall"
        title="Test call"
        ok-title="Place test call"
        cancel-title="Cancel"
        :ok-disabled="!canRunTestCall || testingCall"
        @ok="runTestCall"
      >
        <p class="small text-muted mb-1">
          We’ll place one call so you can hear the script.
        </p>
        <b-alert
          v-if="!testCallOutboundDid"
          show
          variant="warning"
          class="py-50 px-75 mb-1 small"
        >
          {{ testCallDidWarning }}
        </b-alert>
        <b-alert
          v-else-if="jobAgentId && (agentOutboundNumber || agentInboundNumber)"
          show
          variant="light"
          class="py-50 px-75 mb-1 small"
        >
          <div v-if="agentOutboundNumber">
            <strong>They’ll see:</strong> {{ agentOutboundNumber }}
          </div>
          <div v-if="agentInboundNumber" class="mt-25">
            <strong>Company line:</strong> {{ agentInboundNumber }}
          </div>
        </b-alert>
        <b-alert
          v-else-if="testCallOutboundDid"
          show
          variant="light"
          class="py-50 px-75 mb-1 small"
        >
          <strong>They’ll see:</strong> {{ formatIndianPhoneDisplay(testCallOutboundDid) }}
        </b-alert>
        <b-form-group
          label="Company phone line"
          label-for="ai-test-agent-number"
        >
          <b-form-input
            id="ai-test-agent-number"
            :value="testCallAgentNumberDisplay"
            readonly
            disabled
            placeholder="Configure in Admin Settings → AI Calling"
          />
          <small class="text-muted">
            The number candidates will see. Set this in Admin Settings → AI Calling.
          </small>
        </b-form-group>
        <b-form-group
          label="Your phone number"
          label-for="ai-test-candidate-phone"
        >
          <b-form-input
            id="ai-test-candidate-phone"
            v-model="testCallCandidatePhone"
            type="tel"
            maxlength="15"
            placeholder="e.g. 9876543210"
            autocomplete="tel"
            :state="testCallPhoneFieldState"
          />
          <b-form-invalid-feedback>
            Enter a 10-digit mobile number.
          </b-form-invalid-feedback>
          <small v-if="testCallPhoneFieldState !== false" class="text-muted">
            We’ll place one call so you can hear the script.
          </small>
        </b-form-group>
        <b-alert v-if="!jobAgentId" show variant="warning" class="mb-1 py-50 small">
          This job is not linked to an agent yet. Close and use Test call again, or assign an agent in Admin Settings → AI Calling.
        </b-alert>
        <b-alert v-else-if="preparingStep" show variant="info" class="mb-0 py-50 small">
          Setting up job agent…
        </b-alert>
      </b-modal>
    </template>
  </div>
</template>

<script>
import {
  BBadge,
  BAlert,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
  BProgress,
  BProgressBar,
  BModal,
  BCollapse,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import adminConfigService from '@/services/admin-config-service'
import aiCallingService, {
  isAiCallingNotDeployed,
  isInitiateCallSuccess,
  getAiCallingErrorMessage,
  getTestCallSuccessMessage,
  getTestCallBlockedMessage,
  parseAgentPhoneNumbersResponse,
  parseIndusAgentsListResponse,
  formatIndianPhoneDisplay,
  normalizePhoneForIndus,
  resolveTestCallCandidateId,
  extractBoundAgentId,
  excludeDeletedIndusAgents,
  isDeletedIndusAgent,
  rememberDeletedIndusAgents,
  AGENTS_LIST_UNAVAILABLE_MSG,
} from '@/services/ai-calling-service'
import {
  canViewAiCalling,
  canEditAiPrompt,
  canRunAiCalling,
} from '@/utils/ai-calling-permissions'
import {
  DEFAULT_FIRST_MESSAGE,
  DEFAULT_SYSTEM_PROMPT,
  AI_CALL_TYPES,
  resolvePromptForCall,
  TEST_CALL_DEMO_CANDIDATE_NAME,
  DEFAULT_AGENT_NAME,
  humanAgentName,
  looksLikeAgentCode,
  buildJobPromptValues,
  normalizeCallInfieldsMap,
  slimInitiateInfields,
  extractDeclaredInfieldKeys,
  getDefaultFirstMessage,
  getDefaultSystemPrompt,
  getCallTypeLabel,
  getPresetPromptLines,
  normalizeCallType,
  isStockHinglishPrompt,
  resolveInitiateTranscriptLanguage,
  looksLikeEnglishAgent,
  normalizeAgentVoiceMap,
  languageDisplayLabel,
  canonicalizeIndusVoiceId,
  isBrokenHindiConfigAgent,
  DEFAULT_ENGLISH_VOICE_ID,
} from '@/utils/ai-calling-prompt-defaults'
import { validateAiCallingPrompt } from '@/utils/ai-calling-prompt-validator'
import {
  resolveAgentReadyDisplay,
  resolveAgentReadyLabel,
} from '@/utils/ai-calling-readiness'
import { parseScriptFlow, syncFlowStepsToPrompts } from '@/utils/ai-calling-script-parser'
import AiCallingCallTypePicker from '@/views/apps/jobs/jobs-view/components/AiCallingCallTypePicker.vue'
import AiCallingAgentPicker from '@/views/apps/jobs/jobs-view/components/AiCallingAgentPicker.vue'
import AiCallingSimplePromptPicker from '@/views/apps/jobs/jobs-view/components/AiCallingSimplePromptPicker.vue'
import AiCallingVariableEditor from '@/views/apps/jobs/jobs-view/components/AiCallingVariableEditor.vue'
import AiCallingScriptFlow from '@/views/apps/jobs/jobs-view/components/AiCallingScriptFlow.vue'
import AiCallingCallHistory from '@/views/apps/jobs/jobs-view/components/AiCallingCallHistory.vue'

const STATUS_OPTIONS = [
  { title: 'UploadedBySourcer' },
  { title: 'UploadedByRecruiter' },
  { title: 'AssignedByMassMail' },
  { title: 'Wrong Number' },
  { title: 'Not Answered' },
  { title: 'Not Reachable' },
  { title: 'Not Interested' },
  { title: 'Not Relevant' },
  { title: 'Call Back' },
  { title: 'ProfileShared' },
  { title: 'ReviewReject' },
  { title: 'Initiated' },
  { title: 'ScreenReject' },
  { title: 'ScreenSelect' },
  { title: 'Interview' },
  { title: 'No Show' },
  { title: 'Duplicate' },
  { title: 'L1 Select' },
  { title: 'L1 Reject' },
  { title: 'L2 Select' },
  { title: 'L2 Reject' },
  { title: 'Final Select' },
  { title: 'Inprocess' },
  { title: 'OnHold' },
  { title: 'Shortlisted' },
  { title: 'Selected' },
  { title: 'Offer Accepted' },
  { title: 'Draft Offer' },
  { title: 'Offered' },
  { title: 'NotOffered' },
  { title: 'Joined' },
  { title: 'OfferDrop' },
  { title: 'Rejected' },
  { title: 'Early Attrition' },
  { title: 'Attrition' },
  { title: 'Confirmed' },
  { title: 'LOI Issued' },
  { title: 'Document Submitted' },
  { title: 'Drop' },
]

/** Cap client-side prompt history stored in jobScripts JSON (no versions API). */
const PROMPT_HISTORY_MAX = 10
const PROMPT_PERSIST_DEBOUNCE_MS = 700
const AGENT_SETUP_DEBOUNCE_MS = 280
const AGENTS_EMPTY_MSG = 'No default agent is assigned for this call type. Ask an admin to set one.'

export default {
  name: 'JobAiCallingTab',
  components: {
    BBadge,
    BAlert,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    BProgress,
    BProgressBar,
    BModal,
    BCollapse,
    vSelect,
    AiCallingCallTypePicker,
    AiCallingAgentPicker,
    AiCallingSimplePromptPicker,
    AiCallingVariableEditor,
    AiCallingScriptFlow,
    AiCallingCallHistory,
  },
  props: {
    jobId: {
      type: [String, Number],
      required: true,
    },
    jobTitle: {
      type: String,
      default: '',
    },
    clientName: {
      type: [String, Array, Object],
      default: '',
    },
    location: {
      type: [String, Array],
      default: '',
    },
    role: {
      type: [String, Array, Object],
      default: '',
    },
    category: {
      type: String,
      default: '',
    },
    vertical: {
      type: String,
      default: '',
    },
    domain: {
      type: String,
      default: '',
    },
    jobType: {
      type: String,
      default: '',
    },
    minExp: {
      type: [String, Number],
      default: '',
    },
    maxExp: {
      type: [String, Number],
      default: '',
    },
    minCtc: {
      type: [String, Number],
      default: '',
    },
    maxCtc: {
      type: [String, Number],
      default: '',
    },
    desiredSkills: {
      type: [String, Array],
      default: '',
    },
    qualification: {
      type: String,
      default: '',
    },
    companyName: {
      type: String,
      default: '',
    },
    driveName: {
      type: String,
      default: '',
    },
    driveDates: {
      type: [String, Array],
      default: '',
    },
    candidates: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      specialUsers: [],
      configWarning: '',
      adminEnabled: true,
      adminAgentId: '',
      adminAgentIdFresh: '',
      adminAgentIdDrive: '',
      adminAgentIdOther: '',
      adminAgentName: '',
      adminVoice: '',
      adminTtsDefaults: {
        model: 'indus-tts-v1',
        outputFormat: 'mp3',
        speed: 1,
        pitchShift: 0,
        loudnessDb: 0,
        sampleRate: 24000,
      },
      jobAgentId: '',
      jobAgentName: '',
      jobAgentCallType: '',
      availableAgents: [],
      agentsLoading: false,
      agentsLoadError: '',
      agentsListUnavailable: false,
      selectedAgentId: '',
      typedAgentId: '',
      loadingJobAgent: false,
      agentSetupStatus: '',
      agentSetupError: '',
      agentSetupSeq: 0,
      agentSetupTimer: null,
      sessionBlocked: false,
      activeSessionUserName: '',
      sessionHeartbeatTimer: null,
      adminCompanyName: '',
      adminContactNumber: '',
      adminOutboundNumbers: [],
      agentInboundNumber: '',
      agentOutboundNumber: '',
      loadingAgentPhones: false,
      adminTranscriptLanguage: '',
      adminCallingSchema: null,
      adminAgentVoiceMap: {},
      adminAgentLabelOverrides: {},
      adminFirstMessageTemplate: '',
      adminSystemPromptTemplate: '',
      adminDriveFirstMessageTemplate: '',
      adminDriveSystemPromptTemplate: '',
      flowPhase: 'prompt',
      promptJobDetailsOpen: false,
      editJobDetailsOpen: false,
      editScriptOpen: false,
      selectedPresetId: '',
      customPromptBrief: '',
      promptGenerateTimer: null,
      promptPersistTimer: null,
      restoringPrompts: false,
      promptMetaByType: {
        fresh: { presetId: '', customBrief: '', otherBrief: '', promptSource: '' },
        drive: { presetId: '', customBrief: '', otherBrief: '', promptSource: '' },
        other: { presetId: '', customBrief: '', otherBrief: '', promptSource: '' },
      },
      /** True only when GET …/scripts returned a non-empty script for that call type (not admin defaults). */
      scriptsFromApi: {
        fresh: false,
        drive: false,
        other: false,
      },
      promptHistory: [],
      generatingPrompt: false,
      otherCallLabel: '',
      previewingStepId: '',
      editableVars: {
        client: '',
        role: '',
        location: '',
        industry: '',
        agent_name: '',
        company: '',
        min_experience_years: '',
        preferred_qualification: '',
        drive_name: '',
        drive_dates: '',
      },
      preparingStep: false,
      callType: '',
      selectedStatuses: ['Initiated', 'Call Back', 'Not Answered'],
      statusOptions: STATUS_OPTIONS,
      script: '',
      resolvedSystemPrompt: '',
      scripts: {
        fresh: '',
        drive: '',
        other: '',
      },
      systemPrompts: {
        fresh: '',
        drive: '',
        other: '',
      },
      defaultScripts: {
        fresh: '',
        drive: '',
        other: '',
      },
      defaultSystemPrompts: {
        fresh: '',
        drive: '',
        other: '',
      },
      eligibleCount: 0,
      queueStatus: {
        freeLines: 2,
        busyLines: 0,
        queueDepth: 0,
      },
      activeCampaign: null,
      progress: {
        queued: 0,
        calling: 0,
        completed: 0,
        failed: 0,
      },
      previewing: false,
      previewUrl: null,
      starting: false,
      acting: false,
      showConfirm: false,
      showTestCall: false,
      testCallCandidatePhone: '',
      testingCall: false,
      pollTimer: null,
      queueTimer: null,
      callHistoryOpen: false,
      batchDataLoaded: false,
    }
  },
  computed: {
    canView() {
      return canViewAiCalling(this.$can.bind(this), this.specialUsers)
    },
    canEditPrompt() {
      return canEditAiPrompt(this.$can.bind(this), this.specialUsers)
    },
    canRun() {
      return canRunAiCalling(this.$can.bind(this), this.specialUsers) && this.adminEnabled
    },
    /** Pick the job’s one agent + initiate. Not org Create Agent / config. */
    canUseJobCalling() {
      return this.canEditPrompt || this.canRun
    },
    callTypeLabel() {
      if (!this.hasCallType) return 'Call'
      return getCallTypeLabel(this.callType)
    },
    hasCallType() {
      const t = (this.callType || '').toString().trim()
      return t === AI_CALL_TYPES.FRESH
        || t === AI_CALL_TYPES.DRIVE
        || t === AI_CALL_TYPES.OTHER
        || t === 'follow_up'
    },
    isVoiceAgentReady() {
      return this.agentSetupStatus === 'ready' && !!this.jobAgentId
    },
    showAgentStep() {
      return this.hasCallType
    },
    hasSelectedAgent() {
      return !!(this.selectedAgentId || this.typedAgentId || '').toString().trim()
    },
    orgAssignedAgentId() {
      return this.resolveOrgAgentIdForCallType(this.callType)
    },
    pickerAgents() {
      const matching = this.availableAgents.filter(a => (
        a.id && this.agentMatchesCallType(a, this.callType)
      ))
      const stored = (this.jobAgentId || '').toString().trim()
      if (stored && !matching.some(a => a.id === stored)) {
        matching.unshift({
          id: stored,
          name: this.jobAgentName || this.resolveOrgAgentNameForCallType(this.callType),
          description: '',
          callType: this.callType,
          isDefault: stored === this.orgAssignedAgentId,
        })
      }
      return matching
    },
    showTypedAgentFallback() {
      return this.agentsListUnavailable && !this.agentsLoading
    },
    showAgentListRetry() {
      return this.hasCallType
        && !this.agentsLoading
        && (this.agentsListUnavailable || this.pickerAgents.length === 0)
    },
    agentsEmptyMessage() {
      return this.agentsLoadError || AGENTS_EMPTY_MSG
    },
    agentPickerHint() {
      if (this.agentsLoading) return 'Loading your agents…'
      if (this.hasSelectedAgent) {
        return 'This agent will use this job’s role, location, and opening line.'
      }
      if (this.showTypedAgentFallback) {
        return 'Could not load agents. Enter the id an admin gave you, or try again.'
      }
      return 'Pick the one agent that will make this call. Screening uses a single agent.'
    },
    scriptValidation() {
      return this.buildScriptValidation().validation
    },
    scriptValidationOk() {
      return this.scriptValidation.valid === true
    },
    agentReadyDisplayStatus() {
      return resolveAgentReadyDisplay({
        agentSetupStatus: this.agentSetupStatus,
        generatingPrompt: this.generatingPrompt,
        scriptValidationOk: this.scriptValidationOk,
      })
    },
    selectedAgentReadyLabel() {
      return resolveAgentReadyLabel({
        agentSetupStatus: this.agentSetupStatus,
        generatingPrompt: this.generatingPrompt,
        scriptValidationOk: this.scriptValidationOk,
        validationError: this.scriptValidation.errors[0] || '',
      })
    },
    selectedAgentVoiceLine() {
      const id = this.resolveDialAgentId()
      if (!id) return ''
      const lang = languageDisplayLabel(this.resolveDialTranscriptLanguage())
      return `${id} · transcript ${lang}. Spoken language and voice come from this agent’s Indus Config.`
    },
    brokenHindiConfigDial() {
      return isBrokenHindiConfigAgent(this.resolveDialAgentId())
    },
    brokenHindiConfigHint() {
      return 'If Test Call fails, set this agent’s Indus Config Language to English and Voice Maya (Indus-en-maya). ATS still sends transcript_language en.'
    },
    showScriptStep() {
      if (!this.hasCallType || !this.hasSelectedAgent) return false
      return true
    },
    callTypePickerHint() {
      return 'Pick one. The next questions open below.'
    },
    setupAsideStatus() {
      if (!this.hasCallType) return 'Pick a call type on the left to continue.'
      if (this.agentsLoading || this.agentSetupStatus === 'setting_up') return 'Getting the agent ready…'
      if (this.agentSetupStatus === 'error') return 'Agent setup failed. Retry on the left.'
      if (!this.isVoiceAgentReady) return 'Pick one agent, then the script will appear.'
      if (this.generatingPrompt) return 'Updating the script…'
      if (this.isVoiceAgentReady && !this.scriptValidationOk) {
        return this.scriptValidation.errors[0]
          || 'Job details are missing — fill them before the test call.'
      }
      if (!this.isStep1Complete) return 'Review the opening line, then test the call.'
      return 'Ready — hear a test call before you start.'
    },
    setupSteps() {
      const typeDone = this.hasCallType
      const agentDone = this.hasSelectedAgent && this.isVoiceAgentReady
      const scriptDone = this.isStep1Complete
      return [
        { id: 'type', label: 'Type', done: typeDone, current: !typeDone },
        { id: 'agent', label: 'Agent', done: agentDone, current: typeDone && !agentDone },
        { id: 'script', label: 'Script', done: scriptDone, current: agentDone && !scriptDone },
        { id: 'test', label: 'Test call', done: false, current: scriptDone },
      ]
    },
    hasScriptChoice() {
      // Opening-line presets are optional; a default template is enough once an agent is picked.
      return this.hasSelectedAgent
    },
    transcriptLanguageLabel() {
      return `Transcript · ${languageDisplayLabel(this.resolveDialTranscriptLanguage())}`
    },
    canGetTestCall() {
      if (this.flowPhase !== 'prompt' && this.flowPhase !== 'edit') return false
      if (!this.hasCallType || !this.hasSelectedAgent || !this.isVoiceAgentReady) return false
      return this.canUseJobCalling
        && !!(this.script || '').trim()
        && !!(this.resolvedSystemPrompt || '').trim()
        && !this.generatingPrompt
    },
    isStep1Complete() {
      if (!this.hasCallType || !this.hasSelectedAgent || !this.isVoiceAgentReady) return false
      return !!(this.script || '').trim()
        && !!(this.resolvedSystemPrompt || '').trim()
        && !!(this.promptPreviewOpening || '').trim()
        && !this.generatingPrompt
    },
    testCallPromptValues() {
      return {
        ...this.jobPromptValues,
        candidate_name: this.resolveTestCandidateName(),
      }
    },
    promptPreviewOpening() {
      if (!this.hasCallType || !this.hasScriptChoice) return ''
      const ct = normalizeCallType(this.callType)
      const firstTpl = this.resolveFirstMessageTemplate(ct)
      if (!firstTpl) return ''
      return resolvePromptForCall(firstTpl, this.testCallPromptValues)
    },
    scriptFlowSteps() {
      const ct = normalizeCallType(this.callType)
      const firstTpl = this.resolveFirstMessageTemplate(ct)
      return parseScriptFlow({
        firstMessage: firstTpl,
        systemPrompt: this.resolvedSystemPrompt || this.resolveActiveSystemPrompt(),
        promptValues: this.testCallPromptValues,
      })
    },
    jobPromptOverrides() {
      const overrides = {}
      Object.entries(this.editableVars || {}).forEach(([key, val]) => {
        if (val != null && String(val).trim()) overrides[key] = String(val).trim()
      })
      overrides.agent_name = this.resolveSpeakingAgentName()
      if (!overrides.company) {
        overrides.company = this.companyName || this.adminCompanyName || ''
      }
      return overrides
    },
    jobPromptValues() {
      return buildJobPromptValues(this.jobRecord, this.jobPromptOverrides)
    },
    jobContext() {
      return {
        job_id: this.jobId,
        ...this.jobPromptValues,
      }
    },
    jobRecord() {
      return {
        position_name: this.jobTitle,
        client: this.clientName,
        clientName: this.clientName,
        role: this.role,
        locations: this.location,
        location: this.location,
        vertical: this.vertical,
        category: this.category,
        domain: this.domain,
        type: this.jobType,
        min_exp: this.minExp,
        max_exp: this.maxExp,
        min_ctc: this.minCtc,
        max_ctc: this.maxCtc,
        minCtc: this.minCtc,
        maxCtc: this.maxCtc,
        desired_skills: this.desiredSkills,
        qualification: this.qualification,
        drive_name: this.driveName,
        drive_dates: this.driveDates,
        driveName: this.driveName,
        driveDates: this.driveDates,
      }
    },
    orgFirstMessage() {
      return this.resolveOrgFirstMessageTemplate(this.callType)
    },
    orgSystemPrompt() {
      return this.resolveOrgSystemPromptTemplate(this.callType)
    },
    linesStatusLabel() {
      const free = Number(this.queueStatus.freeLines)
      const busy = Number(this.queueStatus.busyLines)
      const depth = Number(this.queueStatus.queueDepth) || 0
      if (Number.isNaN(free) && Number.isNaN(busy)) return 'Lines: checking…'
      if (busy >= 2 || free <= 0) {
        return depth > 0 ? `Both busy · queued (~${depth})` : 'Both busy · queued'
      }
      if (busy === 1) return '1 busy · 1 free'
      return '2 lines free'
    },
    canStart() {
      return this.canRun
        && !this.sessionBlocked
        && this.selectedStatuses.length > 0
        && this.eligibleCount > 0
        && !!(this.script || '').trim()
        && !!(this.resolvedSystemPrompt || this.resolveActiveSystemPrompt()).trim()
        && !this.generatingPrompt
        && this.flowPhase === 'configure'
    },
    testCallCandidates() {
      if (this.poolCandidates.length) return this.poolCandidates
      return []
    },
    poolCandidates() {
      return this.filterPoolCandidates(this.candidates)
    },
    testCallAgentNumberDisplay() {
      const digits = this.resolveTestCallAgentNumber()
      return digits ? formatIndianPhoneDisplay(digits) : ''
    },
    testCallCandidatePhoneValid() {
      return this.isValidIndianPhoneInput(this.testCallCandidatePhone)
    },
    testCallPhoneFieldState() {
      if (!(this.testCallCandidatePhone || '').trim()) return null
      return this.testCallCandidatePhoneValid === true ? null : false
    },
    testCallPhoneHelp() {
      const raw = (this.testCallCandidatePhone || '').trim()
      if (raw && this.testCallCandidatePhoneValid !== true) return 'Enter a 10-digit mobile number.'
      return 'We’ll call this number so you can hear the script.'
    },
    canPlaceTestCall() {
      return this.canGetTestCall
        && this.testCallCandidatePhoneValid === true
        && !this.preparingStep
        && !this.sessionBlocked
        && !this.generatingPrompt
        && !this.testingCall
    },
    testCallButtonTitle() {
      if (this.canPlaceTestCall) return ''
      if (!(this.testCallCandidatePhone || '').trim()) return 'Enter your phone number first'
      if (this.testCallCandidatePhoneValid !== true) return 'Enter a 10-digit mobile number'
      return ''
    },
    testCallOutboundDid() {
      return this.resolveOutboundDid()
    },
    testCallDidWarning() {
      return 'No outbound phone number is set. Add numbers in Admin Settings → AI Calling, then try again.'
    },
    canRunTestCall() {
      return this.testCallCandidatePhoneValid === true
        && !!this.jobAgentId
        && !!this.testCallOutboundDid
        && !this.testingCall
        && !this.preparingStep
    },
    progressTotal() {
      return (this.progress.queued || 0)
        + (this.progress.calling || 0)
        + (this.progress.completed || 0)
        + (this.progress.failed || 0)
    },
    isCampaignActive() {
      const status = String(this.activeCampaign?.status || '').toLowerCase()
      return ['queued', 'running', 'in_progress', 'paused', 'active'].includes(status)
    },
    campaignStatusLabel() {
      return this.activeCampaign?.status || 'unknown'
    },
    campaignStatusVariant() {
      const status = String(this.activeCampaign?.status || '').toLowerCase()
      if (status === 'completed' || status === 'done') return 'light-success'
      if (status === 'failed' || status === 'cancelled') return 'light-danger'
      if (status === 'paused') return 'light-warning'
      return 'light-info'
    },
    flowPhaseSubtitle() {
      if (this.flowPhase === 'approve') {
        return 'The test call is done. Start calling candidates, or edit the script first.'
      }
      if (this.flowPhase === 'edit') {
        return 'Adjust job details and the script, then place another test call.'
      }
      if (this.flowPhase === 'configure') {
        return 'Choose who to include, then start calling.'
      }
      return 'Answer a few questions, then hear a test call before you start.'
    },
    jobDetailsSummary() {
      const values = this.jobPromptValues || {}
      const parts = [values.client, values.role, values.location, values.agent_name]
        .map(part => String(part || '').trim())
        .filter(Boolean)
      return parts.join(' · ') || 'Click to review job details'
    },
    scriptAccordionSummary() {
      const steps = this.scriptFlowSteps || []
      const firstLabel = steps[0] && steps[0].label
        ? String(steps[0].label).replace(/\{\{\s*[^}]+\s*\}\}/g, '').replace(/\s+/g, ' ').trim()
        : ''
      const countLabel = `${steps.length} step${steps.length === 1 ? '' : 's'}`
      return firstLabel ? `${firstLabel} · ${countLabel}` : countLabel
    },
  },
  watch: {
    jobId: {
      immediate: true,
      handler() {
        this.bootstrap()
      },
    },
    flowPhase(phase) {
      if (phase === 'edit' || phase === 'configure') {
        this.editJobDetailsOpen = false
        this.editScriptOpen = false
      }
      if (phase === 'configure') {
        this.ensureBatchDataLoaded()
      } else if (this.queueTimer) {
        // Stop queue polling outside batch configure — avoid background churn.
        clearInterval(this.queueTimer)
        this.queueTimer = null
      }
    },
    candidates: {
      deep: false,
      handler() {
        if (this.flowPhase === 'configure') {
          this.refreshEligibleCount()
        }
      },
    },
    jobTitle() {
      this.refreshComposedDefaultsIfNeeded()
    },
    clientName() {
      this.refreshComposedDefaultsIfNeeded()
    },
    location: {
      deep: false,
      handler() {
        this.refreshComposedDefaultsIfNeeded()
      },
    },
    role() {
      this.refreshComposedDefaultsIfNeeded()
    },
    vertical() {
      this.refreshComposedDefaultsIfNeeded()
    },
    category() {
      this.refreshComposedDefaultsIfNeeded()
    },
    minExp() {
      this.refreshComposedDefaultsIfNeeded()
    },
    maxExp() {
      this.refreshComposedDefaultsIfNeeded()
    },
    minCtc() {
      this.refreshComposedDefaultsIfNeeded()
    },
    maxCtc() {
      this.refreshComposedDefaultsIfNeeded()
    },
    desiredSkills() {
      this.refreshComposedDefaultsIfNeeded()
    },
    driveName() {
      this.refreshComposedDefaultsIfNeeded()
    },
    editableVars: {
      deep: true,
      handler() {
        this.refreshComposedScriptDisplay()
      },
    },
  },
  beforeDestroy() {
    this.clearTimers()
    clearTimeout(this.promptGenerateTimer)
    clearTimeout(this.promptPersistTimer)
    clearTimeout(this.agentSetupTimer)
    this.releaseSession()
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }
  },
  methods: {
    formatIndianPhoneDisplay,
    async onCallTypeChange(type) {
      const prevType = normalizeCallType(this.callType)
      const ct = normalizeCallType(type)
      this.callType = type
      this.flowPhase = 'prompt'
      this.promptJobDetailsOpen = false
      const meta = this.promptMetaByType[ct] || {}
      this.selectedPresetId = meta.presetId || ''
      this.customPromptBrief = meta.customBrief || ''
      this.otherCallLabel = meta.otherBrief || ''
      const savedFirst = (this.scripts[ct] || '').trim()
      const savedSys = (this.systemPrompts[ct] || '').trim()
      const keepCustom = savedFirst && !isStockHinglishPrompt(savedFirst)
      if (keepCustom) {
        this.script = this.composeFromTemplate(savedFirst)
        this.resolvedSystemPrompt = savedSys || this.resolveActiveSystemPrompt()
      } else {
        this.scripts[ct] = getDefaultFirstMessage(ct)
        this.systemPrompts[ct] = savedSys && !isStockHinglishPrompt(savedSys)
          ? savedSys
          : getDefaultSystemPrompt(ct)
        this.script = this.composeFromTemplate(this.scripts[ct])
        this.resolvedSystemPrompt = this.systemPrompts[ct]
      }
      if (prevType === ct && this.isVoiceAgentReady && this.hasSelectedAgent) {
        this.schedulePersistPrompt()
        if (this.hasSelectedAgent && !savedFirst) {
          this.ensureDefaultOpeningLine()
        }
        return
      }
      if (this.availableAgents.length || this.agentsLoading) {
        this.applyDefaultAgentSelection()
      } else {
        await this.loadAvailableAgents()
      }
      this.schedulePersistPrompt()
      if (this.hasSelectedAgent && !savedFirst) {
        this.ensureDefaultOpeningLine()
      }
    },
    formatAgentCallTypeLabel(callType) {
      const ct = normalizeCallType(callType)
      if (ct === AI_CALL_TYPES.DRIVE) return 'Drive'
      if (ct === AI_CALL_TYPES.OTHER) return 'Other'
      if (ct === AI_CALL_TYPES.FRESH) return 'Screening'
      return ''
    },
    normalizePickerAgent(raw) {
      if (raw == null) return { id: '', name: '', description: '', callType: '', isDefault: false }
      if (typeof raw === 'string') {
        return { id: raw, name: raw, description: '', callType: '', isDefault: false }
      }
      const nested = raw.agent_in && typeof raw.agent_in === 'object' ? raw.agent_in : {}
      const id = (
        raw.agent_id || raw.agentId || raw.id || raw.value || nested.agent_id || ''
      ).toString().trim()
      const override = id && this.adminAgentLabelOverrides
        ? String(this.adminAgentLabelOverrides[id] || '').trim()
        : ''
      const name = (
        override
        || raw.name || raw.agent_name || raw.title || nested.name || nested.agent_name || ''
      ).toString().trim()
      const callType = (raw.call_type || raw.callType || nested.call_type || nested.callType || '').toString().trim()
      const description = (
        raw.description || raw.agent_description || nested.description || ''
      ).toString().trim()
      const isDefault = !!(
        raw.is_default || raw.isDefault || nested.is_default || nested.isDefault
      )
      return {
        id,
        name: name || id,
        description: description || this.formatAgentCallTypeLabel(callType),
        callType,
        isDefault,
        declaredInfields: extractDeclaredInfieldKeys(raw),
      }
    },
    agentMatchesCallType(agent, callType) {
      const ct = normalizeCallType(callType)
      const raw = ((agent && (agent.callType || agent.call_type)) || '').toString().trim().toLowerCase()
      if (!raw) return true
      if (raw === 'fresh' || raw === 'screening' || raw.includes('screen')) {
        return ct === AI_CALL_TYPES.FRESH
      }
      if (raw === 'drive' || raw === 'follow_up' || raw.includes('drive')) {
        return ct === AI_CALL_TYPES.DRIVE
      }
      if (raw === 'other' || raw.includes('other') || raw.includes('custom')) {
        return ct === AI_CALL_TYPES.OTHER
      }
      return normalizeCallType(raw) === ct
    },
    resolvePreferredAgentId(callType) {
      const stored = (this.jobAgentId || '').toString().trim()
      if (!stored) return ''
      const inPicker = this.pickerAgents.some(a => a.id === stored)
        || this.availableAgents.some(a => a.id === stored && this.agentMatchesCallType(a, callType))
      if (inPicker) return stored
      if (!this.availableAgents.length) return stored
      return ''
    },
    normalizeAgentsList(rawList) {
      const seen = new Set()
      const out = []
      excludeDeletedIndusAgents(Array.isArray(rawList) ? rawList : []).forEach(item => {
        const agent = this.normalizePickerAgent(item)
        if (!agent.id || seen.has(agent.id)) return
        seen.add(agent.id)
        out.push(agent)
      })
      return out
    },
    collectAgentRows(res) {
      if (!res) return []
      const parsed = parseIndusAgentsListResponse(res)
      const fromParse = parsed.ok ? parsed.agents : []
      const nested = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
        ? res.data
        : res
      const agents = Array.isArray(nested.agents) ? nested.agents : fromParse
      return agents.length ? agents : fromParse
    },
    async loadAvailableAgents() {
      this.agentsLoading = true
      this.agentsLoadError = ''
      this.agentsListUnavailable = false
      try {
        const availableRes = await aiCallingService.listAvailableAgents(this.jobId)
        const rows = this.collectAgentRows(availableRes)
        if (!rows.length) {
          this.availableAgents = []
          const unavailable = isAiCallingNotDeployed(availableRes)
            || (availableRes && availableRes.httpStatus >= 400 && !availableRes.empty)
          this.agentsListUnavailable = unavailable
          this.agentsLoadError = unavailable
            ? AGENTS_LIST_UNAVAILABLE_MSG
            : AGENTS_EMPTY_MSG
          if (unavailable) {
            // eslint-disable-next-line no-console
            console.error('[JobAiCallingTab] agents list unavailable', {
              httpStatus: availableRes?.httpStatus,
              message: getAiCallingErrorMessage(availableRes, 'ai-calling/agents/available'),
            })
          }
          return
        }
        this.availableAgents = this.normalizeAgentsList(rows)
        this.agentsListUnavailable = this.availableAgents.length === 0
        if (!this.availableAgents.length) {
          this.agentsLoadError = AGENTS_EMPTY_MSG
        }
      } catch (e) {
        this.availableAgents = []
        this.agentsListUnavailable = true
        this.agentsLoadError = AGENTS_EMPTY_MSG
        // eslint-disable-next-line no-console
        console.error('[JobAiCallingTab] agents list failed', e)
      } finally {
        this.agentsLoading = false
        this.applyDefaultAgentSelection()
      }
    },
    isLivePickerAgent(id) {
      const want = String(id || '').trim()
      return !!(want && this.availableAgents.some(a => a.id === want))
    },
    applyDefaultAgentSelection() {
      if (!this.hasCallType) return
      if (!this.agentsListUnavailable && this.jobAgentId && !this.isLivePickerAgent(this.jobAgentId)) {
        this.jobAgentId = ''
        this.jobAgentName = ''
      }
      const inPicker = (id) => {
        const want = String(id || '').trim()
        return !!(want && this.pickerAgents.some(a => a.id === want))
      }
      const pick = (id, { clearTyped = false } = {}) => {
        if (!id || !inPicker(id)) return false
        this.selectedAgentId = id
        if (clearTyped) this.typedAgentId = ''
        this.ensureDefaultOpeningLine()
        if (this.jobAgentId === id) {
          this.agentSetupStatus = 'ready'
          return true
        }
        this.scheduleVoiceAgentSetup()
        return true
      }
      const stored = this.resolvePreferredAgentId(this.callType)
      if (stored && pick(stored, { clearTyped: true })) return
      if (this.selectedAgentId && inPicker(this.selectedAgentId)) {
        this.ensureDefaultOpeningLine()
        return
      }
      const typed = (this.typedAgentId || '').toString().trim()
      if (typed && pick(typed)) return
      if (this.pickerAgents.length === 1) {
        pick(this.pickerAgents[0].id, { clearTyped: true })
        return
      }
      this.selectedAgentId = ''
      if (this.agentSetupStatus === 'setting_up' || this.agentSetupStatus === 'ready') {
        this.agentSetupStatus = ''
      }
    },
    ensureDefaultOpeningLine() {
      if (!this.hasCallType) return
      const ct = normalizeCallType(this.callType)
      if (ct !== AI_CALL_TYPES.OTHER && !(this.selectedPresetId || '').trim()) {
        this.selectedPresetId = 'default'
        this.syncPromptMetaFromUi()
      }
      if (
        !(this.script || '').trim()
        || !(this.resolvedSystemPrompt || '').trim()
        || isStockHinglishPrompt(this.scripts[ct] || this.script)
      ) {
        if (isStockHinglishPrompt(this.scripts[ct] || '')) {
          this.scripts[ct] = getDefaultFirstMessage(ct)
        }
        if (isStockHinglishPrompt(this.systemPrompts[ct] || '')) {
          this.systemPrompts[ct] = getDefaultSystemPrompt(ct)
        }
        this.refreshFromTemplates()
      }
    },
    resolveSelectedAgentName() {
      const id = (this.selectedAgentId || this.jobAgentId || '').toString().trim()
      const override = id && this.adminAgentLabelOverrides
        ? String(this.adminAgentLabelOverrides[id] || '').trim()
        : ''
      if (override) return override
      const found = this.availableAgents.find(a => a.id === id)
      if (found && found.name) return found.name
      if (this.jobAgentName) return this.jobAgentName
      return this.resolveOrgAgentNameForCallType(this.callType)
    },
    resolveSpeakingAgentName() {
      return humanAgentName(
        this.editableVars && this.editableVars.agent_name,
        this.resolveSelectedAgentName(),
        this.jobAgentName,
        this.adminAgentName,
        DEFAULT_AGENT_NAME,
      )
    },
    onAgentSelected(id) {
      const agentId = (id || '').toString().trim()
      this.selectedAgentId = agentId
      this.typedAgentId = ''
      this.ensureDefaultOpeningLine()
      if (agentId) {
        this.scheduleVoiceAgentSetup({ keepReady: this.jobAgentId === agentId })
      }
    },
    onTypedAgentId(val) {
      this.typedAgentId = val
      const agentId = (val || '').toString().trim()
      this.selectedAgentId = agentId
      this.ensureDefaultOpeningLine()
      if (agentId) {
        this.scheduleVoiceAgentSetup({ keepReady: this.jobAgentId === agentId })
      } else if (this.agentSetupStatus === 'setting_up') {
        this.agentSetupStatus = ''
      }
    },
    scheduleVoiceAgentSetup({ keepReady = false } = {}) {
      clearTimeout(this.agentSetupTimer)
      this.agentSetupError = ''
      const seq = this.agentSetupSeq + 1
      this.agentSetupSeq = seq
      if (!(keepReady && this.jobAgentId && this.agentSetupStatus === 'ready')) {
        this.agentSetupStatus = 'setting_up'
      }
      this.agentSetupTimer = setTimeout(() => {
        this.runVoiceAgentSetup(seq)
      }, AGENT_SETUP_DEBOUNCE_MS)
    },
    retryVoiceAgentSetup() {
      if (this.sessionBlocked || !this.canUseJobCalling || !this.hasCallType) return
      if (!this.hasSelectedAgent) {
        this.loadAvailableAgents()
        return
      }
      this.scheduleVoiceAgentSetup()
    },
    isStaleAgentSetup(seq) {
      return seq != null && seq !== this.agentSetupSeq
    },
    humanizeAgentSetupError(raw) {
      const msg = (raw || '').toString().trim()
      if (/field required|valid dictionary|valid object|action_type|agent_name|agent_type|call_infield|unprocessable/i.test(msg)) {
        return msg
      }
      if (!msg || /https?:|\/api\/|\bGET\b|\bPOST\b|\b404\b|\b422\b|ai-calls|ai-calling/i.test(msg)) {
        return 'Could not set up this agent. Try again, or pick a different one.'
      }
      return msg
    },
    async runVoiceAgentSetup(seq) {
      if (this.isStaleAgentSetup(seq) || !this.hasCallType) return
      if (this.sessionBlocked || !this.canUseJobCalling) {
        if (this.jobAgentId) this.agentSetupStatus = 'ready'
        return
      }
      const requestedType = normalizeCallType(this.callType)
      this.agentSetupStatus = 'setting_up'
      this.agentSetupError = ''
      try {
        const ok = await this.ensureJobAgent({ silent: true, setupSeq: seq })
        if (this.isStaleAgentSetup(seq)) return
        if (normalizeCallType(this.callType) !== requestedType) return
        if (ok && this.jobAgentId) {
          this.agentSetupStatus = 'ready'
          this.agentSetupError = ''
          return
        }
        this.agentSetupStatus = 'error'
        this.agentSetupError = this.humanizeAgentSetupError(this.agentSetupError)
      } catch (e) {
        if (this.isStaleAgentSetup(seq)) return
        this.agentSetupStatus = 'error'
        this.agentSetupError = 'Could not set up the voice agent. Try again.'
      }
    },
    onPromptChange() {
      if (!this.hasScriptChoice) {
        clearTimeout(this.promptGenerateTimer)
        this.script = ''
        this.resolvedSystemPrompt = ''
        this.syncPromptMetaFromUi()
        this.schedulePersistPrompt()
        return
      }
      this.syncPromptMetaFromUi()
      this.schedulePromptGeneration()
    },
    schedulePromptGeneration() {
      if (!this.hasCallType || !this.hasScriptChoice) return
      clearTimeout(this.promptGenerateTimer)
      this.promptGenerateTimer = setTimeout(() => {
        this.generatePromptForPhase1()
      }, 500)
    },
    schedulePersistPrompt() {
      if (this.restoringPrompts || !this.canEditPrompt || !this.hasCallType) return
      clearTimeout(this.promptPersistTimer)
      this.promptPersistTimer = setTimeout(() => {
        this.saveJobScript({ silent: true })
      }, PROMPT_PERSIST_DEBOUNCE_MS)
    },
    syncPromptMetaFromUi() {
      if (!this.hasCallType) return
      const ct = normalizeCallType(this.callType)
      this.$set(this.promptMetaByType, ct, {
        presetId: this.selectedPresetId || '',
        customBrief: this.customPromptBrief || '',
        otherBrief: this.otherCallLabel || '',
        promptSource: this.resolvePromptSource(),
      })
    },
    resolvePromptSource() {
      const ct = normalizeCallType(this.callType)
      if (ct === AI_CALL_TYPES.OTHER) {
        return (this.otherCallLabel || '').trim() ? 'custom' : ''
      }
      if (this.selectedPresetId === 'custom') {
        return (this.customPromptBrief || '').trim() ? 'gemini' : 'custom'
      }
      if (this.selectedPresetId) return 'preset'
      return ''
    },
    resolveActiveBrief() {
      if (!this.hasCallType) return ''
      const ct = normalizeCallType(this.callType)
      if (ct === AI_CALL_TYPES.OTHER) {
        return (this.otherCallLabel || '').trim()
      }
      if (this.selectedPresetId === 'custom') {
        return (this.customPromptBrief || '').trim()
      }
      const preset = getPresetPromptLines(ct).find(p => p.id === this.selectedPresetId)
      return (preset?.brief || '').trim()
    },
    refreshFromTemplates() {
      if (!this.hasCallType) return
      const ct = normalizeCallType(this.callType)
      const firstTpl = this.resolveFirstMessageTemplate(ct)
      const sysTpl = this.resolveSystemPromptTemplate(ct)
      this.script = this.composeFromTemplate(firstTpl)
      this.resolvedSystemPrompt = sysTpl
      if (firstTpl) this.scripts[ct] = firstTpl
      if (sysTpl) this.systemPrompts[ct] = sysTpl
    },
    async generatePromptForPhase1() {
      if (!this.hasCallType || !this.hasScriptChoice) {
        this.script = ''
        this.resolvedSystemPrompt = ''
        return
      }
      const ct = normalizeCallType(this.callType)
      const brief = this.resolveActiveBrief()

      if (ct === AI_CALL_TYPES.OTHER && !brief) {
        this.refreshFromTemplates()
        this.syncPromptMetaFromUi()
        this.schedulePersistPrompt()
        return
      }

      if (!brief) {
        this.refreshFromTemplates()
        this.syncPromptMetaFromUi()
        this.schedulePersistPrompt()
        return
      }

      this.generatingPrompt = true
      try {
        const res = await aiCallingService.generatePromptWithGemini({
          callType: ct,
          brief,
          jobContext: this.jobContext,
        })
        if (isAiCallingNotDeployed(res)) {
          this.applyPromptGenerationFallback(
            getAiCallingErrorMessage(res) || 'AI script helper unavailable — using default template.',
          )
          return
        }
        const data = res.data || res
        const systemPrompt = data.systemPrompt || data.system_prompt || ''
        const firstMessage = data.firstMessage || data.first_message || ''
        if (!systemPrompt || !firstMessage) {
          this.applyPromptGenerationFallback(
            getAiCallingErrorMessage(res) || 'Could not generate script — using default template.',
          )
          return
        }
        this.scripts[ct] = firstMessage
        this.systemPrompts[ct] = systemPrompt
        this.script = resolvePromptForCall(firstMessage, this.jobPromptValues)
        this.resolvedSystemPrompt = systemPrompt
        this.syncPromptMetaFromUi()
        this.schedulePersistPrompt()
      } catch (e) {
        this.applyPromptGenerationFallback('Could not generate script — using default template.')
      } finally {
        this.generatingPrompt = false
      }
    },
    applyPromptGenerationFallback(message) {
      this.refreshFromTemplates()
      this.syncPromptMetaFromUi()
      this.schedulePersistPrompt()
      if (message) {
        this.$bvToast.toast(message, {
          title: 'AI Calling',
          variant: 'warning',
          solid: true,
        })
      }
    },
    approveCall() {
      this.flowPhase = 'configure'
      this.ensureBatchDataLoaded()
    },
    rejectCall() {
      this.flowPhase = 'edit'
    },
    finishEditing() {
      this.flowPhase = 'approve'
    },
    onScriptFlowStepEdit({ id, text }) {
      const ct = normalizeCallType(this.callType)
      const currentSteps = this.scriptFlowSteps.map(step => (
        step.id === id ? { ...step, templateText: text } : step
      ))
      const synced = syncFlowStepsToPrompts(currentSteps, {
        firstMessage: this.resolveFirstMessageTemplate(ct),
        systemPrompt: this.resolvedSystemPrompt || this.resolveSystemPromptTemplate(ct),
      })
      this.scripts[ct] = synced.firstMessage
      this.systemPrompts[ct] = synced.systemPrompt
      this.resolvedSystemPrompt = synced.systemPrompt
      this.script = resolvePromptForCall(synced.firstMessage, this.jobPromptValues)
      this.syncPromptMetaFromUi()
      this.schedulePersistPrompt()
    },
    backToPromptPhase() {
      this.flowPhase = 'prompt'
      if (this.jobAgentId) {
        this.agentSetupStatus = 'ready'
        this.agentSetupError = ''
      }
    },
    onStatusesChange(statuses) {
      this.selectedStatuses = statuses
      this.refreshEligibleCount()
    },
    /**
     * Lazy-load batch/campaign/queue data only when entering configure.
     * Avoids parallel heavy requests on tab open.
     */
    async ensureBatchDataLoaded() {
      if (this.flowPhase !== 'configure') return
      if (!this.batchDataLoaded) {
        this.batchDataLoaded = true
        await Promise.all([
          this.loadQueueStatus(),
          this.loadLatestCampaign(),
          this.refreshEligibleCount(),
        ])
        if (this.activeCampaign && this.isCampaignActive) {
          this.startPolling()
        }
      } else {
        await this.refreshEligibleCount()
        await this.loadQueueStatus()
      }
      if (!this.queueTimer) {
        this.queueTimer = setInterval(this.loadQueueStatus, 15000)
      }
    },
    initEditableVars() {
      const base = buildJobPromptValues(this.jobRecord, {
        agent_name: this.adminAgentName,
        company: this.companyName || this.adminCompanyName,
      })
      this.editableVars = {
        client: base.client || '',
        role: base.role || '',
        location: base.location || '',
        industry: base.industry || '',
        agent_name: base.agent_name || '',
        company: base.company || '',
        min_experience_years: base.min_experience_years || '',
        max_experience_years: base.max_experience_years || '',
        min_ctc: base.min_ctc || '',
        max_ctc: base.max_ctc || '',
        preferred_qualification: base.preferred_qualification || '',
        drive_name: base.drive_name || '',
        drive_dates: base.drive_dates || '',
      }
    },
    refreshComposedScriptDisplay() {
      const ct = normalizeCallType(this.callType)
      const firstTpl = this.resolveFirstMessageTemplate(ct)
      if (firstTpl) {
        this.script = this.composeFromTemplate(firstTpl)
      }
    },
    resolveOrgFirstMessageTemplate(callType) {
      const ct = normalizeCallType(callType)
      if (ct === AI_CALL_TYPES.DRIVE) {
        const drive = this.adminDriveFirstMessageTemplate
        return drive && !isStockHinglishPrompt(drive)
          ? drive
          : getDefaultFirstMessage(AI_CALL_TYPES.DRIVE)
      }
      if (ct === AI_CALL_TYPES.OTHER) {
        const other = this.scripts.other
        return other && !isStockHinglishPrompt(other)
          ? other
          : getDefaultFirstMessage(AI_CALL_TYPES.FRESH)
      }
      const fresh = this.adminFirstMessageTemplate
      return fresh && !isStockHinglishPrompt(fresh)
        ? fresh
        : getDefaultFirstMessage(ct)
    },
    resolveOrgSystemPromptTemplate(callType) {
      const ct = normalizeCallType(callType)
      if (ct === AI_CALL_TYPES.DRIVE) {
        return this.defaultSystemPrompts.drive || getDefaultSystemPrompt(AI_CALL_TYPES.DRIVE)
      }
      if (ct === AI_CALL_TYPES.OTHER) {
        return this.systemPrompts.other || getDefaultSystemPrompt(AI_CALL_TYPES.FRESH)
      }
      const admin = this.adminSystemPromptTemplate
      return admin && !isStockHinglishPrompt(admin)
        ? admin
        : getDefaultSystemPrompt(ct)
    },
    resolveFirstMessageTemplate(ct) {
      const type = normalizeCallType(ct)
      return this.scripts[type]
        || this.defaultScripts[type]
        || this.resolveOrgFirstMessageTemplate(type)
    },
    resolveSystemPromptTemplate(ct) {
      const type = normalizeCallType(ct)
      return this.systemPrompts[type]
        || this.defaultSystemPrompts[type]
        || this.resolveOrgSystemPromptTemplate(type)
    },
    async applyScriptsForCallType() {
      if (!this.hasCallType) return
      const ct = normalizeCallType(this.callType)
      const savedFirst = (this.scripts[ct] || '').trim()
      const savedSys = (this.systemPrompts[ct] || '').trim()
      // Prefer custom job scripts. Stock Hindi defaults switch to English.
      if (savedFirst && !isStockHinglishPrompt(savedFirst)) {
        this.script = this.composeFromTemplate(savedFirst)
        this.resolvedSystemPrompt = savedSys || this.resolveActiveSystemPrompt()
        return
      }
      if (this.hasScriptChoice) {
        await this.generatePromptForPhase1()
      }
    },
    pickCandidatePk(c) {
      if (!c || typeof c !== 'object') return null
      // JobsView: `id` is the candidate PK used on routes; `candidate_id` is display.
      const candidates = [c.id, c.candidateId, c.candidate_id]
      for (let i = 0; i < candidates.length; i += 1) {
        const n = Number(candidates[i])
        if (Number.isFinite(n) && n > 0) return n
      }
      return null
    },
    resolveTestCandidateRecord() {
      const all = Array.isArray(this.candidates) ? this.candidates : []
      const testPhone = normalizePhoneForIndus(this.testCallCandidatePhone)
      if (!testPhone) return null
      return all.find((c) => {
        const phone = normalizePhoneForIndus(c.contact_no || c.contactNo || c.phone || c.mobile)
        return phone && phone === testPhone
      }) || null
    },
    resolveTestCandidateId() {
      const match = this.resolveTestCandidateRecord()
      const list = this.testCallCandidates || []
      return resolveTestCallCandidateId({
        matchedCandidateId: this.pickCandidatePk(match),
        fallbackCandidateId: list.length ? this.pickCandidatePk(list[0]) : null,
        jobId: this.jobId,
      })
    },
    resolveTestJobId() {
      const jobN = Number(this.jobId)
      return Number.isFinite(jobN) && jobN > 0 ? jobN : this.jobId
    },
    resolveTestCandidateName() {
      const match = this.resolveTestCandidateRecord()
      if (match) {
        const name = this.resolvePoolCandidateName(match)
        if (name && name !== 'Unknown') return name
        const display = this.resolvePoolCandidateDisplayName({
          name,
          contact_no: match.contact_no || match.contactNo || match.phone || match.mobile,
          id: this.pickCandidatePk(match),
        })
        if (display && display !== 'Unknown') return display
      }
      return TEST_CALL_DEMO_CANDIDATE_NAME
    },
    resolveTestCallUserName() {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}') || {}
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
          || user.emp_name
          || user.fullName
          || user.name
          || user.username
          || ''
        if (name) return name
      } catch (e) {
        /* ignore */
      }
      return this.resolveTestCandidateName()
    },
    resolveTestCallAgentNumber() {
      const sources = [
        this.agentInboundNumber,
        this.adminContactNumber,
        this.agentOutboundNumber,
        ...(this.adminOutboundNumbers || []),
      ]
      for (let i = 0; i < sources.length; i += 1) {
        const digits = String(sources[i] || '').replace(/\D/g, '').slice(-10)
        if (digits.length === 10) return digits
      }
      return ''
    },
    isValidIndianPhoneInput(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      if (!digits) return null
      if (digits.length === 10) return true
      if (digits.length === 12 && digits.startsWith('91')) return true
      return false
    },
    poolCandidateOptionKey(option) {
      return String(option?.id ?? option?.contact_no ?? option?.label ?? '')
    },
    phoneDigitsOnly(phone) {
      return String(phone || '').replace(/\D/g, '')
    },
    phoneLastFour(phone) {
      const digits = this.phoneDigitsOnly(phone)
      return digits.length >= 4 ? digits.slice(-4) : ''
    },
    resolvePoolCandidateName(c) {
      const first = (c.first_name || c.firstName || '').toString().trim()
      const last = (c.last_name || c.lastName || '').toString().trim()
      const combined = [first, last].filter(Boolean).join(' ').trim()
      const alt = (
        c.name
        || c.candidate_name
        || c.full_name
        || c.fullName
        || ''
      ).toString().trim()
      if (combined) return combined
      if (alt) return alt
      return 'Unknown'
    },
    resolvePoolCandidateDisplayName(entry) {
      const name = (entry.name || '').toString().trim()
      if (name && name !== 'Unknown') return name
      const last4 = this.phoneLastFour(entry.contact_no)
      if (last4) return `···${last4}`
      if (entry.id != null && entry.id !== '') return `#${entry.id}`
      return 'Unknown'
    },
    buildPoolCandidateLabel({ name, status, contact_no, id }) {
      const displayName = (name && name !== 'Unknown')
        ? name
        : (this.phoneLastFour(contact_no)
          ? `···${this.phoneLastFour(contact_no)}`
          : (id != null && id !== '' ? `#${id}` : 'Unknown'))
      const statusPart = (status || 'Unknown status').toString().trim()
      return `${displayName} · ${statusPart}`
    },
    dedupePoolCandidates(list) {
      const seen = new Set()
      const out = []
      list.forEach((item, index) => {
        const key = String(item.id ?? item.candidate_id ?? item.contact_no ?? `row-${index}`)
        if (seen.has(key)) return
        seen.add(key)
        out.push(item)
      })
      return out
    },
    buildResolvedCallPrompts(values, { keepTokens = [] } = {}) {
      const ct = normalizeCallType(this.callType)
      const firstTpl = this.resolveFirstMessageTemplate(ct)
      const systemTpl = (
        this.systemPrompts[ct]
        || this.resolvedSystemPrompt
        || this.resolveSystemPromptTemplate(ct)
        || ''
      )
      const firstMessage = resolvePromptForCall(firstTpl, values, { keepTokens }).trim()
      const systemPrompt = resolvePromptForCall(systemTpl, values, { keepTokens }).trim()
      const flow = parseScriptFlow({
        firstMessage: firstTpl,
        systemPrompt: systemTpl,
        promptValues: values,
      }).map(step => ({
        id: step.id,
        kind: step.kind,
        label: step.label,
        text: step.spokenText,
        spokenText: step.spokenText,
      }))
      return {
        script: firstMessage,
        firstMessage,
        first_message: firstMessage,
        starting_instructions: firstMessage,
        systemPrompt,
        system_prompt: systemPrompt,
        callType: ct,
        script_flow: flow,
        conversation_flow: flow,
        questions: flow.filter(step => step.kind === 'question'),
      }
    },
    buildTestCallPromptPayload() {
      return this.buildResolvedCallPrompts(this.testCallPromptValues)
    },
    resolveOutboundDid() {
      const fromAgent = String(this.agentOutboundNumber || '').replace(/\D/g, '')
      if (fromAgent.length >= 10) {
        return normalizePhoneForIndus(fromAgent.slice(-10))
      }
      const numbers = this.adminOutboundNumbers.length
        ? this.adminOutboundNumbers
        : [this.adminContactNumber].filter(Boolean)
      for (let i = 0; i < numbers.length; i += 1) {
        const digits = String(numbers[i] || '').replace(/\D/g, '')
        if (digits.length >= 10) {
          return normalizePhoneForIndus(digits.slice(-10))
        }
      }
      return ''
    },
    buildCallInfields(values = null, { includeCandidateName = true } = {}) {
      const v = values || this.testCallPromptValues
      const infields = normalizeCallInfieldsMap({
        ...this.jobPromptValues,
        ...v,
        agent_name: this.resolveSpeakingAgentName(),
        candidate_name: includeCandidateName
          ? (
            (v && v.candidate_name)
            || this.resolveTestCandidateName()
            || ''
          )
          : (v && v.candidate_name) || '',
      })
      if (!includeCandidateName) {
        delete infields.candidate_name
      }
      return infields
    },
    resolveDeclaredInfieldKeys() {
      const id = this.resolveDialAgentId()
      const found = id ? this.availableAgents.find(a => a.id === id) : null
      if (found && Array.isArray(found.declaredInfields) && found.declaredInfields.length) {
        return found.declaredInfields
      }
      return extractDeclaredInfieldKeys({})
    },
    buildDialInfields(values = null, opts = {}) {
      return slimInitiateInfields(
        this.buildCallInfields(values, opts),
        this.resolveDeclaredInfieldKeys(),
        {
          callType: this.callType,
          config: this.adminCallingSchema,
        },
      )
    },
    buildScriptValidation() {
      const ct = normalizeCallType(this.callType)
      const firstTpl = this.resolveFirstMessageTemplate(ct)
      const systemPrompt = this.resolvedSystemPrompt || this.resolveSystemPromptTemplate(ct)
      return {
        firstTpl,
        systemPrompt,
        validation: validateAiCallingPrompt({
          callType: this.callType,
          systemPrompt,
          firstMessage: firstTpl,
          providedVariables: ['candidate_name'],
          providedValues: this.testCallPromptValues,
        }),
      }
    },
    async handleTestCall() {
      this.preparingStep = true
      try {
        const { firstTpl, validation } = this.buildScriptValidation()
        const testValues = this.testCallPromptValues
        if (!validation.valid) {
          this.$bvToast.toast(
            validation.errors[0] || 'Script validation failed.',
            { title: 'AI Calling', variant: 'warning', solid: true },
          )
          return
        }
        this.script = resolvePromptForCall(firstTpl, testValues)
        const infields = this.buildDialInfields(testValues)
        const agentOk = await this.ensureJobAgent({
          forceRebind: true,
          infields,
        })
        if (agentOk) {
          const readyToDial = this.testCallCandidatePhoneValid === true
            && !!this.jobAgentId
            && !!this.testCallOutboundDid
            && !this.testingCall
          if (readyToDial) {
            this.preparingStep = false
            await this.runTestCall()
          } else {
            await this.openTestCallModal()
          }
        }
      } finally {
        this.preparingStep = false
      }
    },
    clearTimers() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      if (this.queueTimer) {
        clearInterval(this.queueTimer)
        this.queueTimer = null
      }
      if (this.sessionHeartbeatTimer) {
        clearInterval(this.sessionHeartbeatTimer)
        this.sessionHeartbeatTimer = null
      }
      if (this.promptPersistTimer) {
        clearTimeout(this.promptPersistTimer)
        this.promptPersistTimer = null
      }
      if (this.agentSetupTimer) {
        clearTimeout(this.agentSetupTimer)
        this.agentSetupTimer = null
      }
    },
    composeFromTemplate(template) {
      return resolvePromptForCall(template || '', this.jobPromptValues)
    },
    resolveActiveScript() {
      const ct = normalizeCallType(this.callType)
      return this.composeFromTemplate(this.resolveFirstMessageTemplate(ct)) || ''
    },
    resolveActiveSystemPrompt() {
      const ct = normalizeCallType(this.callType)
      return this.resolveSystemPromptTemplate(ct)
    },
    applyDefaultScriptsFromAdmin() {
      const freshTpl = (
        this.adminFirstMessageTemplate && !isStockHinglishPrompt(this.adminFirstMessageTemplate)
      ) ? this.adminFirstMessageTemplate : DEFAULT_FIRST_MESSAGE
      const driveTpl = (
        this.adminDriveFirstMessageTemplate && !isStockHinglishPrompt(this.adminDriveFirstMessageTemplate)
      ) ? this.adminDriveFirstMessageTemplate : getDefaultFirstMessage(AI_CALL_TYPES.DRIVE)
      const sysFresh = (
        this.adminSystemPromptTemplate && !isStockHinglishPrompt(this.adminSystemPromptTemplate)
      ) ? this.adminSystemPromptTemplate : getDefaultSystemPrompt(AI_CALL_TYPES.FRESH)
      const sysDrive = (
        this.adminDriveSystemPromptTemplate && !isStockHinglishPrompt(this.adminDriveSystemPromptTemplate)
      ) ? this.adminDriveSystemPromptTemplate : getDefaultSystemPrompt(AI_CALL_TYPES.DRIVE)
      this.defaultScripts = {
        fresh: freshTpl,
        drive: driveTpl,
        other: freshTpl,
      }
      this.defaultSystemPrompts = {
        fresh: sysFresh,
        drive: sysDrive,
        other: sysFresh,
      }
      if (!this.scripts.fresh || isStockHinglishPrompt(this.scripts.fresh)) this.scripts.fresh = freshTpl
      if (!this.scripts.drive || isStockHinglishPrompt(this.scripts.drive)) this.scripts.drive = driveTpl
      if (!this.scripts.other || isStockHinglishPrompt(this.scripts.other)) this.scripts.other = freshTpl
      if (!this.systemPrompts.fresh || isStockHinglishPrompt(this.systemPrompts.fresh)) this.systemPrompts.fresh = sysFresh
      if (!this.systemPrompts.drive || isStockHinglishPrompt(this.systemPrompts.drive)) this.systemPrompts.drive = sysDrive
      if (!this.systemPrompts.other || isStockHinglishPrompt(this.systemPrompts.other)) this.systemPrompts.other = sysFresh
      this.script = this.resolveActiveScript()
      this.resolvedSystemPrompt = this.resolveActiveSystemPrompt()
    },
    refreshComposedDefaultsIfNeeded() {
      if (!this.adminFirstMessageTemplate && !this.defaultScripts.fresh) return
      const freshTpl = (
        this.adminFirstMessageTemplate && !isStockHinglishPrompt(this.adminFirstMessageTemplate)
      ) ? this.adminFirstMessageTemplate : DEFAULT_FIRST_MESSAGE
      const driveTpl = (
        this.adminDriveFirstMessageTemplate && !isStockHinglishPrompt(this.adminDriveFirstMessageTemplate)
      ) ? this.adminDriveFirstMessageTemplate : getDefaultFirstMessage(AI_CALL_TYPES.DRIVE)
      const prevFresh = this.defaultScripts.fresh
      const prevDrive = this.defaultScripts.drive
      this.defaultScripts = { ...this.defaultScripts, fresh: freshTpl, drive: driveTpl }
      if (!this.scripts.fresh || this.scripts.fresh === prevFresh) {
        this.scripts.fresh = freshTpl
      }
      if (!this.scripts.drive || this.scripts.drive === prevDrive) {
        this.scripts.drive = driveTpl
      }
      const ct = normalizeCallType(this.callType)
      const prevComposedFresh = this.composeFromTemplate(prevFresh)
      const prevComposedDrive = this.composeFromTemplate(prevDrive)
      if (!this.script || this.script === prevComposedFresh || this.script === prevComposedDrive) {
        this.script = this.resolveActiveScript()
      }
      if (!this.resolvedSystemPrompt) {
        this.resolvedSystemPrompt = this.resolveActiveSystemPrompt()
      }
      if (ct === AI_CALL_TYPES.FRESH && (!this.systemPrompts.fresh || this.systemPrompts.fresh === this.defaultSystemPrompts.fresh)) {
        this.systemPrompts.fresh = this.defaultSystemPrompts.fresh
      }
    },
    async bootstrap() {
      this.clearTimers()
      this.batchDataLoaded = false
      this.callHistoryOpen = false
      await this.loadAdminConfig()
      this.initEditableVars()
      // Light bootstrap: agent + scripts + session only.
      // Queue / campaign / eligible / call history load later (configure or expand).
      await Promise.all([
        this.loadJobAgent(),
        this.loadScripts(),
        this.checkActiveSession(),
      ])
      await this.applyScriptsForCallType()
      if (this.hasCallType) {
        await this.loadAvailableAgents()
      }
      this.refreshConfigWarning()
      this.startSessionHeartbeat()
      if (this.flowPhase === 'configure') {
        await this.ensureBatchDataLoaded()
      }
    },
    refreshConfigWarning() {
      if (!this.adminEnabled) {
        this.configWarning = 'AI Calling is disabled in Admin Settings.'
      } else {
        this.configWarning = ''
      }
    },
    async loadAdminConfig() {
      try {
        const res = await adminConfigService.getAiCallingConfig()
        if (res && (res.status === 'OK' || res.data)) {
          const data = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
            ? res.data
            : res
          this.specialUsers = Array.isArray(data.specialUsers) ? data.specialUsers : []
          this.adminEnabled = data.enabled !== false && data.enabled !== 0 && data.enabled !== 'false'
          rememberDeletedIndusAgents(data.deletedAgentIds || data.deleted_agent_ids || [])
          const liveOrgId = (id) => {
            const value = String(id || '').trim()
            return value && !isDeletedIndusAgent(value) ? value : ''
          }
          this.adminAgentIdFresh = liveOrgId(
            data.agentIdFresh
            || data.agent_id_fresh
            || data.agentId
            || data.agent_id
            || '',
          )
          this.adminAgentIdDrive = liveOrgId(
            data.agentIdDrive
            || data.agent_id_drive
            || '',
          )
          this.adminAgentIdOther = liveOrgId(
            data.agentIdOther
            || data.agent_id_other
            || '',
          )
          this.adminAgentId = this.adminAgentIdFresh
          this.adminAgentName = (
            data.agentName || data.agent_name || data.defaultAgentName || ''
          ).toString().trim()
          this.adminCompanyName = (
            data.companyName || data.company_name || data.company || ''
          ).toString().trim()
          const adminNumbers = Array.isArray(data.numbers) ? data.numbers : []
          this.adminOutboundNumbers = [
            data.number1,
            data.number2,
            ...adminNumbers,
          ].map(n => String(n || '').trim()).filter(Boolean)
          this.adminContactNumber = (
            this.adminOutboundNumbers[0]
            || data.indusContactNumber
            || data.indus_contact_number
            || ''
          ).toString().trim()
          this.adminTranscriptLanguage = (
            data.transcriptLanguage || data.transcript_language || ''
          ).toString().trim()
          this.adminVoice = canonicalizeIndusVoiceId(data.voice)
          this.adminCallingSchema = data
          this.adminAgentVoiceMap = normalizeAgentVoiceMap(
            data.agentVoiceMap || data.agent_voice_map || {},
            this.availableAgents,
          )
          const labels = data.agentLabelOverrides || data.agent_label_overrides || {}
          this.adminAgentLabelOverrides = labels && typeof labels === 'object' && !Array.isArray(labels)
            ? { ...labels }
            : {}
          this.adminTtsDefaults = {
            model: data.model || 'indus-tts-v1',
            outputFormat: data.outputFormat || data.output_format || 'mp3',
            speed: data.speed != null ? Number(data.speed) : 1,
            pitchShift: data.pitchShift != null ? Number(data.pitchShift)
              : (data.pitch_shift != null ? Number(data.pitch_shift) : 0),
            loudnessDb: data.loudnessDb != null ? Number(data.loudnessDb)
              : (data.loudness_db != null ? Number(data.loudness_db) : 0),
            sampleRate: data.sampleRate || data.sample_rate || 24000,
          }

          const systemPrompt = (
            data.systemPrompt
            || data.defaultSystemPrompt
            || data.system_prompt
            || data.default_system_prompt
            || ''
          ).toString()
          const firstMessage = (
            data.firstMessage
            || data.defaultFirstMessage
            || data.first_message
            || data.default_first_message
            || ''
          ).toString()
          const legacyFresh = (
            data.defaultFreshScript
            || data.default_fresh_script
            || ''
          ).toString()
          const legacyFollow = (
            data.defaultFollowUpScript
            || data.default_follow_up_script
            || data.defaultDriveScript
            || data.default_drive_script
            || ''
          ).toString()

          this.adminSystemPromptTemplate = systemPrompt.trim()
            ? systemPrompt
            : DEFAULT_SYSTEM_PROMPT
          this.adminFirstMessageTemplate = firstMessage.trim()
            ? firstMessage
            : (legacyFresh.trim() || DEFAULT_FIRST_MESSAGE)
          this.adminDriveFirstMessageTemplate = legacyFollow.trim()
            ? legacyFollow
            : getDefaultFirstMessage(AI_CALL_TYPES.DRIVE)
          const driveSys = (
            data.defaultDriveSystemPrompt
            || data.default_drive_system_prompt
            || ''
          ).toString()
          this.adminDriveSystemPromptTemplate = driveSys.trim()
            ? driveSys
            : getDefaultSystemPrompt(AI_CALL_TYPES.DRIVE)

          if (firstMessage.trim()) {
            this.applyDefaultScriptsFromAdmin()
          } else {
            const freshTpl = legacyFresh.trim() || DEFAULT_FIRST_MESSAGE
            const driveTpl = this.adminDriveFirstMessageTemplate
            const sysFresh = this.adminSystemPromptTemplate || getDefaultSystemPrompt(AI_CALL_TYPES.FRESH)
            const sysDrive = this.adminDriveSystemPromptTemplate || getDefaultSystemPrompt(AI_CALL_TYPES.DRIVE)
            this.defaultScripts = { fresh: freshTpl, drive: driveTpl, other: freshTpl }
            this.defaultSystemPrompts = { fresh: sysFresh, drive: sysDrive, other: sysFresh }
            if (!this.scripts.fresh) this.scripts.fresh = freshTpl
            if (!this.scripts.drive) this.scripts.drive = driveTpl
            if (!this.scripts.other) this.scripts.other = freshTpl
            if (!this.systemPrompts.fresh) this.systemPrompts.fresh = sysFresh
            if (!this.systemPrompts.drive) this.systemPrompts.drive = sysDrive
            if (!this.systemPrompts.other) this.systemPrompts.other = sysFresh
            this.script = this.resolveActiveScript()
            this.resolvedSystemPrompt = this.resolveActiveSystemPrompt()
          }

          this.refreshConfigWarning()
        }
      } catch (e) {
        this.adminFirstMessageTemplate = DEFAULT_FIRST_MESSAGE
        this.adminSystemPromptTemplate = DEFAULT_SYSTEM_PROMPT
        this.applyDefaultScriptsFromAdmin()
        this.refreshConfigWarning()
      }
    },
    async loadScripts() {
      if (!this.jobId) return
      this.restoringPrompts = true
      try {
        const res = await aiCallingService.getJobScripts(this.jobId)
        if (res && (res.status === 'OK' || res.data)) {
          const data = res.data || res
          const fresh = (data.fresh || data.fresh_script || data.firstMessage || '').toString()
          const drive = (
            data.drive
            || data.drive_script
            || data.follow_up
            || data.follow_up_script
            || ''
          ).toString()
          const other = (data.other || data.other_script || '').toString()
          const sysFresh = (
            data.freshSystemPrompt
            || data.systemPrompt
            || data.system_prompt
            || ''
          ).toString()
          const sysDrive = (
            data.driveSystemPrompt
            || data.drive_system_prompt
            || ''
          ).toString()
          const sysOther = (
            data.otherSystemPrompt
            || data.other_system_prompt
            || ''
          ).toString()
          const freshTrim = fresh.trim()
          const driveTrim = drive.trim()
          const otherTrim = other.trim()
          this.scripts = {
            fresh: this.normalizeLoadedScriptTemplate('fresh', freshTrim),
            drive: this.normalizeLoadedScriptTemplate('drive', driveTrim),
            other: this.normalizeLoadedScriptTemplate('other', otherTrim),
          }
          if (sysFresh.trim() && !isStockHinglishPrompt(sysFresh)) this.systemPrompts.fresh = sysFresh
          if (sysDrive.trim() && !isStockHinglishPrompt(sysDrive)) this.systemPrompts.drive = sysDrive
          if (sysOther.trim() && !isStockHinglishPrompt(sysOther)) this.systemPrompts.other = sysOther

          if (data.promptMeta && typeof data.promptMeta === 'object' && !Array.isArray(data.promptMeta)) {
            this.promptMetaByType = {
              fresh: { ...(this.promptMetaByType.fresh || {}), ...(data.promptMeta.fresh || {}) },
              drive: { ...(this.promptMetaByType.drive || {}), ...(data.promptMeta.drive || {}) },
              other: { ...(this.promptMetaByType.other || {}), ...(data.promptMeta.other || {}) },
            }
          }
          this.promptHistory = Array.isArray(data.promptHistory) ? data.promptHistory.slice(0, PROMPT_HISTORY_MAX) : []

          const rawLast = (data.lastCallType || data.callType || '').toString().trim()
          if (
            rawLast === AI_CALL_TYPES.FRESH
            || rawLast === AI_CALL_TYPES.DRIVE
            || rawLast === AI_CALL_TYPES.OTHER
            || rawLast === 'follow_up'
          ) {
            const lastCt = normalizeCallType(rawLast)
            this.callType = lastCt
            const meta = this.promptMetaByType[lastCt] || {}
            this.selectedPresetId = meta.presetId || data.presetId || ''
            this.customPromptBrief = meta.customBrief || data.customBrief || ''
            this.otherCallLabel = meta.otherBrief || data.otherBrief || ''
            if ((this.scripts[lastCt] || '').trim()) {
              this.script = this.composeFromTemplate(this.scripts[lastCt])
              this.resolvedSystemPrompt = this.systemPrompts[lastCt] || ''
              // Legacy saves may lack promptMeta — keep progressive UI unlockable.
              if (lastCt === AI_CALL_TYPES.OTHER && !(this.otherCallLabel || '').trim()) {
                this.otherCallLabel = 'Saved script'
              } else if (lastCt !== AI_CALL_TYPES.OTHER && !(this.selectedPresetId || '').trim()) {
                this.selectedPresetId = 'default'
              }
            }
          }
        }
      } catch (e) {
        // keep admin-composed defaults
      } finally {
        this.$nextTick(() => {
          this.restoringPrompts = false
        })
      }
      if (!(this.script || '').trim()) {
        this.script = this.resolveActiveScript()
      }
      if (!(this.resolvedSystemPrompt || '').trim()) {
        this.resolvedSystemPrompt = this.resolveActiveSystemPrompt()
      }
    },
    normalizeLoadedScriptTemplate(type, saved) {
      const ct = normalizeCallType(type)
      const fallback = this.scripts[ct] || this.defaultScripts[ct] || getDefaultFirstMessage(ct)
      if (!saved || isStockHinglishPrompt(saved)) return fallback
      if (saved.includes('{{')) return saved
      const defaultTpl = this.defaultScripts[ct] || ''
      if (defaultTpl.includes('{{')) {
        this.script = saved
        return defaultTpl
      }
      return saved
    },
    applyJobAgentRecord(record, fallbackId = '') {
      const data = record && typeof record === 'object' ? record : {}
      const nextId = extractBoundAgentId(data, fallbackId)
      if (nextId) this.jobAgentId = nextId
      const nextName = (data.agent_name || data.agentName || data.name || '').toString().trim()
      if (nextName) this.jobAgentName = nextName
      const linkedType = (data.call_type || data.callType || '').toString().trim()
      this.jobAgentCallType = linkedType
        ? normalizeCallType(linkedType)
        : normalizeCallType(this.callType)
    },
    resolveDialAgentId() {
      return (
        (this.selectedAgentId || '').toString().trim()
        || (this.typedAgentId || '').toString().trim()
        || (this.jobAgentId || '').toString().trim()
      )
    },
    resolveDialAgentName() {
      const id = this.resolveDialAgentId()
      const found = id ? this.availableAgents.find(a => a.id === id) : null
      return (found && (found.name || found.agent_name)) || ''
    },
    resolveDialTranscriptLanguage() {
      return resolveInitiateTranscriptLanguage({
        transcriptLanguage: this.adminTranscriptLanguage,
        agent_id: this.resolveDialAgentId(),
        agentName: this.resolveDialAgentName(),
        agentVoiceMap: this.adminAgentVoiceMap,
        assignedScreeningId: this.adminAgentIdFresh,
        assignedDriveId: this.adminAgentIdDrive,
        callType: this.callType,
        config: this.adminCallingSchema,
      })
    },
    resolveOrgAgentIdForCallType(callType) {
      const ct = normalizeCallType(callType)
      const pickLive = (...ids) => {
        for (let i = 0; i < ids.length; i += 1) {
          const id = String(ids[i] || '').trim()
          if (id && this.isLivePickerAgent(id)) return id
        }
        return ''
      }
      if (ct === AI_CALL_TYPES.DRIVE) {
        return pickLive(this.adminAgentIdDrive)
      }
      if (ct === AI_CALL_TYPES.OTHER) {
        return pickLive(this.adminAgentIdOther)
      }
      return pickLive(this.adminAgentIdFresh, this.adminAgentId)
    },
    resolveOrgAgentNameForCallType(callType) {
      const ct = normalizeCallType(callType)
      const id = this.resolveOrgAgentIdForCallType(ct)
      const override = id && this.adminAgentLabelOverrides
        ? String(this.adminAgentLabelOverrides[id] || '').trim()
        : ''
      if (override) return override
      const found = id ? this.availableAgents.find(a => a.id === id) : null
      if (found && found.name) return found.name
      if (ct === AI_CALL_TYPES.FRESH && this.adminAgentName) {
        return this.adminAgentName
      }
      if (!id) return ''
      const labels = {
        [AI_CALL_TYPES.FRESH]: 'Screening agent',
        [AI_CALL_TYPES.DRIVE]: 'Drive agent',
        [AI_CALL_TYPES.OTHER]: 'Other agent',
      }
      return labels[ct] || 'Org agent'
    },
    async syncJobAgentForCallType(callType, { silent = true } = {}) {
      const selectedId = (this.selectedAgentId || this.typedAgentId || '').toString().trim()
      const agentId = selectedId || this.resolvePreferredAgentId(callType)
      if (agentId) {
        return this.linkJobToOrgAgent(agentId, { silent, callType })
      }
      if (this.jobAgentId) {
        await this.loadAgentPhoneNumbers()
        return true
      }
      return false
    },
    async loadJobAgent() {
      if (!this.jobId) return
      this.loadingJobAgent = true
      try {
        const res = await aiCallingService.getJobAgent(this.jobId)
        if (res && (res.status === 'OK' || res.data)) {
          const data = res.data || res
          this.applyJobAgentRecord(data)
        }
        if (this.jobAgentId) {
          await this.loadAgentPhoneNumbers()
        }
      } catch (e) {
        // list + user pick will attach a real agent; do not auto-create
      } finally {
        this.loadingJobAgent = false
      }
    },
    async loadAgentPhoneNumbers() {
      if (!this.jobAgentId) {
        this.agentInboundNumber = ''
        this.agentOutboundNumber = ''
        return
      }
      this.loadingAgentPhones = true
      try {
        const res = await aiCallingService.getAgentPhoneNumbers(this.jobAgentId)
        const parsed = parseAgentPhoneNumbersResponse(res)
        if (parsed.ok) {
          this.agentInboundNumber = formatIndianPhoneDisplay(parsed.inbound?.phone_number)
          this.agentOutboundNumber = formatIndianPhoneDisplay(parsed.outbound?.phone_number)
        }
      } catch (e) {
        // non-blocking — test call still works with manual number
      } finally {
        this.loadingAgentPhones = false
      }
    },
    async loadPromptTemplates() {
      // Templates no longer shown in main flow; kept for admin compatibility
    },
    async ensureJobAgent({
      silent = false,
      setupSeq = null,
      forceRebind = false,
      prompt = null,
      infields = null,
    } = {}) {
      if (this.isStaleAgentSetup(setupSeq)) return false
      const agentId = this.resolveDialAgentId()
      if (agentId) {
        return this.linkJobToOrgAgent(agentId, {
          silent,
          callType: this.callType,
          setupSeq,
          forceRebind,
          prompt,
          infields,
        })
      }
      const msg = AGENTS_EMPTY_MSG
      this.agentSetupError = msg
      if (!silent) {
        this.$bvToast.toast(msg, { title: 'AI Calling', variant: 'warning', solid: true })
      }
      return false
    },
    async linkJobToOrgAgent(orgAgentId, {
      silent = true,
      callType = null,
      setupSeq = null,
      forceRebind = false,
      prompt = null,
      infields = null,
    } = {}) {
      const agentId = (orgAgentId || '').toString().trim()
      if (!agentId) return false
      const ct = normalizeCallType(callType != null ? callType : this.callType)
      const mustUpdate = forceRebind || !!(infields && Object.keys(infields).length)

      if (!mustUpdate && this.jobAgentId === agentId) {
        this.jobAgentCallType = ct
        const found = this.availableAgents.find(a => a.id === agentId)
        if (found && found.name) this.jobAgentName = found.name
        await this.loadAgentPhoneNumbers()
        return !this.isStaleAgentSetup(setupSeq)
      }

      try {
        // One org agent owns script + outcomes. Link id + infields only —
        // do not rewrite first_message / system_prompt on Test Call.
        if (this.isStaleAgentSetup(setupSeq)) return false
        const bindPayload = {
          reuse_agent_id: agentId,
          agent_id: agentId,
          call_type: ct,
        }
        if (infields && Object.keys(infields).length) {
          bindPayload.call_infields = infields
        }
        const setRes = await aiCallingService.setJobAgent(this.jobId, bindPayload)
        if (this.isStaleAgentSetup(setupSeq)) return false
        if (setRes && (setRes.status === 'OK' || setRes.agent_id || setRes.data || setRes.status === 200 || setRes.status === 'success')) {
          this.applyJobAgentRecord(setRes.data || setRes, agentId)
          if (!this.jobAgentId) this.jobAgentId = agentId
          this.jobAgentCallType = ct
          const found = this.availableAgents.find(a => a.id === agentId)
          if (found && found.name) {
            this.jobAgentName = found.name
          } else if (!this.jobAgentName) {
            this.jobAgentName = this.resolveOrgAgentNameForCallType(ct)
          }
          await this.loadAgentPhoneNumbers()
          if (!silent) {
            this.$bvToast.toast('Voice agent is ready.', {
              title: 'AI Calling',
              variant: 'success',
              solid: true,
            })
          }
          return true
        }
        if (mustUpdate && agentId) {
          this.jobAgentId = this.jobAgentId || agentId
          await this.loadAgentPhoneNumbers()
          return !this.isStaleAgentSetup(setupSeq)
        }
        const linkMsg = this.humanizeAgentSetupError(
          getAiCallingErrorMessage(setRes)
          || 'Could not attach the voice agent to this job.',
        )
        this.agentSetupError = linkMsg
        if (!silent) {
          this.$bvToast.toast(linkMsg, { title: 'AI Calling', variant: 'warning', solid: true })
        }
        return false
      } catch (e) {
        const failMsg = 'Could not attach the voice agent to this job.'
        this.agentSetupError = failMsg
        if (!silent) {
          this.$bvToast.toast(failMsg, {
            title: 'AI Calling',
            variant: 'danger',
            solid: true,
          })
        }
        return false
      }
    },
    applySessionState(data) {
      const session = data && typeof data === 'object' ? data : {}
      if (!session.active) {
        this.sessionBlocked = false
        this.activeSessionUserName = ''
        return
      }
      const sessionUser = session.user || {}
      const sessionEmpId = (
        sessionUser.emp_id || sessionUser.empId || session.emp_id || session.empId || ''
      ).toString().trim()
      let myEmpId = ''
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        myEmpId = (user.emp_id || '').toString().trim()
      } catch (e) {
        myEmpId = ''
      }
      if (session.is_own || session.isOwn || session.registered) {
        this.sessionBlocked = false
        return
      }
      if (sessionEmpId && myEmpId && sessionEmpId === myEmpId) {
        this.sessionBlocked = false
        return
      }
      this.sessionBlocked = true
      this.activeSessionUserName = sessionUser.userName || sessionUser.name || 'Another user'
    },
    async checkActiveSession() {
      if (!this.jobId) return
      try {
        const res = await aiCallingService.getActiveSession(this.jobId)
        if (res && (res.status === 'OK' || res.data)) {
          this.applySessionState(res.data || res)
        }
      } catch (e) {
        this.sessionBlocked = false
      }
    },
    async heartbeatSession() {
      if (!this.jobId) return
      try {
        const campaignId = this.activeCampaign?.id || this.activeCampaign?.campaignId
        const res = await aiCallingService.registerActiveSession(this.jobId, {
          campaignId: campaignId || undefined,
        })
        if (res && (res.status === 'OK' || res.data)) {
          this.applySessionState(res.data || res)
        }
      } catch (e) {
        // ignore transient errors
      }
    },
    startSessionHeartbeat() {
      if (!this.jobId) return
      this.heartbeatSession()
      this.sessionHeartbeatTimer = setInterval(this.heartbeatSession, 30000)
    },
    async releaseSession() {
      if (!this.jobId) return
      try {
        await aiCallingService.releaseActiveSession(this.jobId)
      } catch (e) {
        // ignore on teardown
      }
    },
    countFromLocalCandidates() {
      if (!this.selectedStatuses.length) return 0
      if (this.candidates.length) {
        return this.filterPoolCandidates(this.candidates).length
      }
      return 0
    },
    filterPoolCandidates(list) {
      if (!this.selectedStatuses.length || !list.length) return []
      const set = new Set(this.selectedStatuses.map(String))
      const mapped = list
        .filter(c => {
          const status = c.candidateStatus || c.status || c.candidate_status
          const phone = c.contact_no || c.contactNo || c.phone || c.mobile
          return set.has(String(status)) && !!phone
        })
        .map(c => {
          const id = c.candidate_id || c.id
          const name = this.resolvePoolCandidateName(c)
          const status = c.candidateStatus || c.status || c.candidate_status
          const contact_no = c.contact_no || c.contactNo || c.phone || c.mobile
          return {
            id,
            name,
            status,
            contact_no,
            label: c.label || this.buildPoolCandidateLabel({
              name,
              status,
              contact_no,
              id,
            }),
          }
        })
      return this.dedupePoolCandidates(mapped)
    },
    async refreshEligibleCount() {
      if (!this.jobId || !this.selectedStatuses.length) {
        this.eligibleCount = 0
        return
      }
      try {
        const res = await aiCallingService.getEligibleCandidates(this.jobId, this.selectedStatuses)
        if (res && (res.status === 'OK' || Array.isArray(res.data) || typeof res.count === 'number')) {
          if (typeof res.count === 'number') {
            this.eligibleCount = res.count
          } else if (typeof res.data?.count === 'number') {
            this.eligibleCount = res.data.count
          } else if (Array.isArray(res.data)) {
            this.eligibleCount = res.data.length
          } else if (Array.isArray(res.candidates)) {
            this.eligibleCount = res.candidates.length
          } else {
            this.eligibleCount = this.countFromLocalCandidates()
          }
          return
        }
      } catch (e) {
        // fall through to local count
      }
      this.eligibleCount = this.countFromLocalCandidates()
    },
    async loadQueueStatus() {
      try {
        const res = await aiCallingService.getQueueStatus()
        if (res && (res.status === 'OK' || res.data || res.freeLines != null)) {
          const data = res.data || res
          this.queueStatus = {
            freeLines: data.freeLines != null ? data.freeLines : 2,
            busyLines: data.busyLines != null ? data.busyLines : 0,
            queueDepth: data.queueDepth != null ? data.queueDepth : 0,
          }
        }
      } catch (e) {
        // keep last known status
      }
    },
    normalizeProgress(data) {
      const p = data.progress || data.counts || data
      return {
        queued: Number(p.queued || p.pending || 0),
        calling: Number(p.calling || p.in_progress || p.running || 0),
        completed: Number(p.completed || p.done || p.success || 0),
        failed: Number(p.failed || p.error || 0),
      }
    },
    async loadLatestCampaign() {
      if (!this.jobId) return
      try {
        const res = await aiCallingService.listJobCampaigns(this.jobId)
        if (res && (res.status === 'OK' || res.data)) {
          const list = Array.isArray(res.data) ? res.data : (res.campaigns || [])
          if (list.length) {
            const latest = list[0]
            this.activeCampaign = latest
            this.progress = this.normalizeProgress(latest)
          }
        }
      } catch (e) {
        // no campaigns yet
      }
    },
    startPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = setInterval(this.pollCampaign, 8000)
    },
    async pollCampaign() {
      const id = this.activeCampaign?.id || this.activeCampaign?.campaignId
      if (!id) return
      try {
        const res = await aiCallingService.getCampaign(id)
        if (res && (res.status === 'OK' || res.data)) {
          const data = res.data || res
          this.activeCampaign = { ...this.activeCampaign, ...data }
          this.progress = this.normalizeProgress(data)
          if (!this.isCampaignActive) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
          }
        }
      } catch (e) {
        // ignore transient poll errors
      }
    },
    campaignAgentConfig() {
      return this.buildCallInfields(this.jobPromptValues, { includeCandidateName: false })
    },
    campaignPromptPayload() {
      return this.buildResolvedCallPrompts({
        ...this.jobPromptValues,
        candidate_name: TEST_CALL_DEMO_CANDIDATE_NAME,
      })
    },
    buildPromptPersistPayload({ forDial = false } = {}) {
      this.syncPromptMetaFromUi()
      const ct = normalizeCallType(this.callType)
      const templateFirst = (this.scripts[ct] || '').trim() || (this.script || '').trim()
      const templateSystem = (
        this.systemPrompts[ct]
        || this.resolvedSystemPrompt
        || this.resolveActiveSystemPrompt()
        || ''
      ).trim()
      const spoken = this.buildResolvedCallPrompts(this.testCallPromptValues)
      const firstMessage = forDial ? spoken.firstMessage : templateFirst
      const systemPrompt = forDial ? spoken.systemPrompt : templateSystem
      const meta = this.promptMetaByType[ct] || {
        presetId: this.selectedPresetId || '',
        customBrief: this.customPromptBrief || '',
        otherBrief: this.otherCallLabel || '',
        promptSource: this.resolvePromptSource(),
      }
      const historyEntry = {
        at: new Date().toISOString(),
        callType: ct,
        presetId: meta.presetId || '',
        customBrief: meta.customBrief || '',
        otherBrief: meta.otherBrief || '',
        promptSource: meta.promptSource || '',
        firstMessage,
        systemPrompt,
      }
      const prevHistory = Array.isArray(this.promptHistory) ? this.promptHistory : []
      const deduped = prevHistory.filter(h => !(
        h
        && h.callType === historyEntry.callType
        && h.firstMessage === historyEntry.firstMessage
        && h.systemPrompt === historyEntry.systemPrompt
      ))
      const promptHistory = firstMessage
        ? [historyEntry, ...deduped].slice(0, PROMPT_HISTORY_MAX)
        : deduped.slice(0, PROMPT_HISTORY_MAX)
      this.promptHistory = promptHistory

      return {
        callType: ct,
        script: firstMessage,
        firstMessage,
        systemPrompt,
        templateFirstMessage: templateFirst,
        templateSystemPrompt: templateSystem,
        spokenFirstMessage: spoken.firstMessage,
        spokenSystemPrompt: spoken.systemPrompt,
        script_flow: spoken.script_flow,
        lastCallType: ct,
        presetId: meta.presetId || '',
        customBrief: meta.customBrief || '',
        otherBrief: meta.otherBrief || '',
        promptSource: meta.promptSource || '',
        promptMeta: { ...this.promptMetaByType },
        promptHistory,
      }
    },
    async saveJobScript({ silent = false, forDial = false } = {}) {
      if (!this.canEditPrompt || this.restoringPrompts) return false
      const payload = this.buildPromptPersistPayload({ forDial })
      if (!(payload.firstMessage || '').trim()) return false
      try {
        const ct = normalizeCallType(this.callType)
        const res = await aiCallingService.saveJobScript(this.jobId, payload)
        if (res && (res.status === 'OK' || res.status === 200 || res.status === 'success')) {
          if (ct === AI_CALL_TYPES.DRIVE) {
            await aiCallingService.saveJobScript(this.jobId, {
              ...payload,
              callType: 'follow_up',
            }).catch(() => {})
          }
          return true
        }
        if (!silent) {
          const msg = getAiCallingErrorMessage(res, `ai-calling/jobs/${this.jobId}/scripts`)
          this.$bvToast.toast(msg, {
            title: 'AI Calling',
            variant: isAiCallingNotDeployed(res) ? 'danger' : 'warning',
            solid: true,
          })
        }
        return false
      } catch (e) {
        if (!silent) {
          this.$bvToast.toast('Could not save script.', {
            title: 'AI Calling',
            variant: 'danger',
            solid: true,
          })
        }
        return false
      }
    },
    async previewFlowStep(step) {
      if (!step || !step.spokenText) return
      this.previewingStepId = step.id
      try {
        const spoken = resolvePromptForCall(
          step.spokenText || step.templateText || '',
          this.testCallPromptValues,
        )
        const res = await aiCallingService.previewSpeech({
          text: spoken,
          callType: normalizeCallType(this.callType),
          firstMessage: spoken,
          systemPrompt: resolvePromptForCall(
            this.resolvedSystemPrompt || this.resolveActiveSystemPrompt(),
            this.testCallPromptValues,
          ),
        })
        if (res instanceof Blob) {
          if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
          this.previewUrl = URL.createObjectURL(res)
          this.$nextTick(() => {
            if (this.$refs.previewAudio) this.$refs.previewAudio.play().catch(() => {})
          })
        } else {
          const msg = (res && (res.message || res.data?.message))
            || "Couldn't play a preview of the voice. Please try again later."
          this.$bvToast.toast(msg, {
            title: 'AI Calling',
            variant: 'info',
            solid: true,
          })
        }
      } finally {
        this.previewingStepId = ''
      }
    },
    async previewVoice() {
      const greeting = this.scriptFlowSteps.find(s => s.kind === 'greeting')
      if (greeting) await this.previewFlowStep(greeting)
    },
    confirmStart() {
      if (!this.canStart) return
      this.showConfirm = true
    },
    refreshCallHistory() {
      const hist = this.$refs.callHistory
      if (hist && typeof hist.loadCalls === 'function') {
        hist.loadCalls()
      }
    },
    onTestCallEnter() {
      if (this.canPlaceTestCall) this.handleTestCall()
    },
    async openTestCallModal() {
      if (this.jobAgentId) {
        await this.loadAgentPhoneNumbers()
      }
      this.showTestCall = true
    },
    normalizePhoneForAiCall(phone) {
      return normalizePhoneForIndus(phone)
    },
    async pushResolvedPromptsToAgent(prompt, infields, explicitAgentId = '') {
      const agentId = (explicitAgentId || this.resolveDialAgentId()).toString().trim()
      if (!agentId) return agentId
      // One org agent owns the script. Link only — do not PATCH prompts or create.
      try {
        const setRes = await aiCallingService.setJobAgent(this.jobId, {
          reuse_agent_id: agentId,
          agent_id: agentId,
          call_type: normalizeCallType(this.callType),
          call_infields: infields,
        })
        const bound = extractBoundAgentId(setRes, agentId)
        if (bound) this.jobAgentId = bound
      } catch (e) {
        if (!this.jobAgentId) this.jobAgentId = agentId
      }
      return this.jobAgentId || agentId
    },
    async runTestCall(bvEvt) {
      if (bvEvt && bvEvt.preventDefault) bvEvt.preventDefault()
      if (!this.canRunTestCall) {
        this.$bvToast.toast(
          getTestCallBlockedMessage({
            jobAgentId: this.jobAgentId,
            did: this.testCallOutboundDid,
          }),
          { title: 'AI Calling', variant: 'warning', solid: true },
        )
        return
      }
      const did = this.testCallOutboundDid
      const selectedId = this.resolveDialAgentId()
      this.testingCall = true
      try {
        const callInfields = this.buildDialInfields()
        const dialAgentId = await this.pushResolvedPromptsToAgent(null, callInfields, selectedId)
        if (dialAgentId) this.jobAgentId = dialAgentId
        if (isBrokenHindiConfigAgent(dialAgentId)) {
          this.$bvToast.toast(
            'If the call fails, set Indus Config Language to English and Voice Maya (Indus-en-maya).',
            { title: 'AI Calling', variant: 'warning', solid: true, autoHideDelay: 10000 },
          )
        }
        // eslint-disable-next-line no-console
        console.info('[JobAiCallingTab] test call bind', {
          selectedAgentId: this.selectedAgentId,
          typedAgentId: this.typedAgentId,
          jobAgentId: this.jobAgentId,
          dialAgentId,
          transcript_language: this.resolveDialTranscriptLanguage(),
          call_infields: callInfields,
        })
        const res = await aiCallingService.initiateCall({
          candidate_id: this.resolveTestCandidateId(),
          job_id: this.resolveTestJobId(),
          contact_no: this.testCallCandidatePhone,
          customer_number: this.testCallCandidatePhone,
          agent_id: dialAgentId,
          agent_number: dialAgentId,
          did,
          transcript: true,
          transcript_language: this.resolveDialTranscriptLanguage(),
          agentVoiceMap: this.adminAgentVoiceMap,
          assignedScreeningId: this.adminAgentIdFresh,
          assignedDriveId: this.adminAgentIdDrive,
          call_type: normalizeCallType(this.callType),
          callType: normalizeCallType(this.callType),
          agent_name: this.resolveDialAgentName(),
          user_name: this.resolveTestCallUserName(),
          candidate_name: this.resolveTestCandidateName(),
          call_infields: callInfields,
          declared_infields: this.resolveDeclaredInfieldKeys(),
          config: this.adminCallingSchema,
        })
        if (isInitiateCallSuccess(res)) {
          this.showTestCall = false
          this.flowPhase = 'approve'
          this.$bvToast.toast(
            getTestCallSuccessMessage(res),
            {
              title: 'AI Calling',
              variant: 'success',
              solid: true,
            },
          )
          this.refreshCallHistory()
        } else {
          const msg = getAiCallingErrorMessage(res, 'ai-calls/initiate')
            || 'Could not place test call. Check agent setup, DID, and backend logs.'
          this.$bvToast.toast(msg, {
            title: 'AI Calling',
            variant: isAiCallingNotDeployed(res) ? 'danger' : 'warning',
            solid: true,
          })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[JobAiCallingTab] test call initiate failed', {
          message: e && e.message,
          httpStatus: e && e.response && e.response.status,
        })
        this.$bvToast.toast(
          (e && e.message) || 'Could not place test call — network or server error.',
          { title: 'AI Calling', variant: 'danger', solid: true },
        )
      } finally {
        this.testingCall = false
      }
    },
    async startCampaign(bvEvt) {
      if (bvEvt && bvEvt.preventDefault) bvEvt.preventDefault()
      if (!this.canStart) return
      this.starting = true
      try {
        const campaignInfields = this.buildDialInfields({
          ...this.jobPromptValues,
        })
        const agentOk = await this.ensureJobAgent({
          forceRebind: true,
          infields: campaignInfields,
        })
        if (!agentOk) return
        const ct = normalizeCallType(this.callType)
        const dialAgentId = await this.pushResolvedPromptsToAgent(
          null,
          campaignInfields,
          this.resolveDialAgentId(),
        )
        const res = await aiCallingService.startCampaign(this.jobId, {
          callType: ct,
          statuses: this.selectedStatuses,
          agent_id: dialAgentId,
          transcript_language: this.resolveDialTranscriptLanguage(),
          agentVoiceMap: this.adminAgentVoiceMap,
          assignedScreeningId: this.adminAgentIdFresh,
          assignedDriveId: this.adminAgentIdDrive,
          agent_name: this.resolveDialAgentName(),
          agent_config: campaignInfields,
          call_infields: campaignInfields,
          declared_infields: this.resolveDeclaredInfieldKeys(),
          config: this.adminCallingSchema,
        })
        if (res && (res.status === 'OK' || res.status === 202 || res.status === 'Accepted' || res.campaignId || res.data)) {
          const data = res.data || res
          this.activeCampaign = {
            id: data.campaignId || data.id || data.campaign_id,
            status: data.status || 'queued',
            callType: ct,
          }
          this.progress = this.normalizeProgress(data)
          if (!this.progressTotal) {
            this.progress.queued = this.eligibleCount
          }
          this.showConfirm = false
          this.$bvToast.toast('Batch started — calling up to 2 people at a time.', {
            title: 'AI Calling',
            variant: 'success',
            solid: true,
          })
          this.startPolling()
          this.loadQueueStatus()
        } else {
          const msg = getAiCallingErrorMessage(res, `ai-calling/jobs/${this.jobId}/campaigns`)
            || 'Could not start batch. Backend campaign API may not be ready yet.'
          this.$bvToast.toast(msg, {
            title: 'AI Calling',
            variant: isAiCallingNotDeployed(res) ? 'danger' : 'warning',
            solid: true,
          })
        }
      } finally {
        this.starting = false
      }
    },
    async pauseCampaign() {
      const id = this.activeCampaign?.id || this.activeCampaign?.campaignId
      if (!id) return
      this.acting = true
      try {
        const res = await aiCallingService.pauseCampaign(id)
        if (res && (res.status === 'OK' || res.status === 200 || res.status === 'success')) {
          this.activeCampaign = { ...this.activeCampaign, status: 'paused' }
          this.$bvToast.toast('Campaign paused.', { title: 'AI Calling', variant: 'warning', solid: true })
        } else {
          this.$bvToast.toast((res && res.message) || 'Pause failed.', {
            title: 'AI Calling',
            variant: 'danger',
            solid: true,
          })
        }
      } finally {
        this.acting = false
      }
    },
    async cancelCampaign() {
      const id = this.activeCampaign?.id || this.activeCampaign?.campaignId
      if (!id) return
      this.acting = true
      try {
        const res = await aiCallingService.cancelCampaign(id)
        if (res && (res.status === 'OK' || res.status === 200 || res.status === 'success')) {
          this.activeCampaign = { ...this.activeCampaign, status: 'cancelled' }
          this.$bvToast.toast('Remaining queued calls cancelled.', {
            title: 'AI Calling',
            variant: 'danger',
            solid: true,
          })
          if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
          }
        } else {
          this.$bvToast.toast((res && res.message) || 'Cancel failed.', {
            title: 'AI Calling',
            variant: 'danger',
            solid: true,
          })
        }
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style scoped>
.ai-calling-tab {
  --ai-primary: #00ac99;
  --ai-primary-soft: rgba(0, 172, 153, 0.1);
  --ai-primary-border: rgba(0, 172, 153, 0.35);
  --ai-text: #4b465c;
  --ai-heading: #2f2b3d;
  --ai-muted: #6e6b7b;
  --ai-border: rgba(47, 43, 61, 0.12);
  --ai-surface: #f8f8f8;
  --ai-radius: 0.5rem;
  color: var(--ai-text);
  font-size: 15px;
  line-height: 1.5;
}

.ai-calling-tab ::v-deep .form-group {
  margin-bottom: 0.75rem;
}
.ai-calling-tab ::v-deep .form-control {
  min-height: 2.5rem;
  font-size: 15px;
  color: var(--ai-text);
}
.ai-calling-tab ::v-deep .vs__dropdown-toggle {
  min-height: 2.5rem;
  font-size: 15px;
  padding: 0.25rem 0.4rem;
}
.ai-calling-tab ::v-deep .vs__selected,
.ai-calling-tab ::v-deep .vs__search {
  font-size: 15px;
  color: var(--ai-text);
}
.ai-calling-tab ::v-deep .ai-field-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--ai-heading);
  margin-bottom: 0.4rem;
}
.ai-calling-tab ::v-deep .btn-primary {
  background-color: var(--ai-primary);
  border-color: var(--ai-primary);
  box-shadow: none;
}
.ai-calling-tab ::v-deep .btn-primary:hover:not(:disabled),
.ai-calling-tab ::v-deep .btn-primary:focus:not(:disabled) {
  background-color: #009886;
  border-color: #009886;
  box-shadow: none;
}

.ai-page-header {
  margin-bottom: 0.75rem;
}
.ai-page-subtitle {
  font-size: 0.9375rem;
  line-height: 1.45;
  color: #5e5873;
  margin: 0;
  max-width: none;
}
.ai-setup-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
  width: 100%;
}
.ai-setup-aside {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}
.ai-aside-card {
  width: 100%;
}
.ai-setup-steps {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ai-setup-steps--aside {
  flex-direction: column;
  align-items: stretch;
  gap: 0.45rem;
}
.ai-setup-steps__item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.55rem 0.3rem 0.3rem;
  border-radius: 999px;
  background: var(--ai-surface);
  color: var(--ai-muted);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}
.ai-setup-steps--aside .ai-setup-steps__item {
  width: 100%;
  border-radius: 0.4rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.8125rem;
}
.ai-setup-steps__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  background: #e9e9ea;
  color: #6e6b7b;
  font-size: 0.6875rem;
}
.ai-setup-steps__item--current {
  background: var(--ai-primary-soft);
  color: #007a6e;
}
.ai-setup-steps__item--current .ai-setup-steps__index {
  background: var(--ai-primary);
  color: #fff;
}
.ai-setup-steps__item--done {
  background: var(--ai-primary-soft);
  color: #007a6e;
}
.ai-setup-steps__item--done .ai-setup-steps__index {
  background: var(--ai-primary);
  color: #fff;
}
.ai-aside-preview__quote {
  margin: 0;
  padding: 0;
  font-size: 15px;
  line-height: 1.55;
  color: var(--ai-text);
  white-space: pre-wrap;
  word-break: break-word;
  font-style: normal;
  border-left: none;
}
.ai-aside-placeholder {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
}
.ai-language-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.5rem;
  flex-shrink: 0;
}
.ai-generating-hint {
  display: flex;
  align-items: center;
  padding: 0.65rem 0;
  color: var(--ai-text);
  font-size: 14px;
}
@media (min-width: 992px) {
  .ai-setup-grid {
    grid-template-columns: minmax(0, 1.25fr) minmax(22rem, 0.85fr);
  }
  .ai-setup-aside {
    position: sticky;
    top: 1rem;
  }
}

.ai-banner {
  display: flex;
  align-items: flex-start;
  padding: 0.7rem 0.9rem;
  font-size: 14px;
  line-height: 1.45;
}

.ai-empty-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1.5rem;
  border: 1px dashed var(--ai-border);
  border-radius: var(--ai-radius);
  background: #fff;
}
.ai-empty-panel__icon {
  color: var(--ai-primary);
  margin-bottom: 0.75rem;
}
.ai-empty-panel__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ai-heading);
  margin-bottom: 0.35rem;
}
.ai-empty-panel__hint {
  font-size: 14px;
  color: var(--ai-muted);
  max-width: 28rem;
}

.ai-editor-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}
.ai-editor-layout__details,
.ai-editor-layout__script {
  min-width: 0;
  width: 100%;
}
.ai-details-accordion__toggle.ai-section-card__header {
  width: 100%;
  margin: 0;
  border: 0;
  border-bottom: 1px solid transparent;
  background: var(--ai-surface);
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  appearance: none;
  -webkit-appearance: none;
}
.ai-details-accordion__toggle[aria-expanded='true'] {
  border-bottom-color: var(--ai-border);
}
.ai-details-accordion__toggle:hover .ai-section-card__title,
.ai-details-accordion__toggle:focus .ai-section-card__title {
  color: var(--ai-primary);
}
.ai-details-accordion__toggle:focus {
  outline: none;
}
.ai-details-accordion__toggle:focus-visible {
  outline: 2px solid #00ac99;
  outline-offset: -2px;
}
.ai-details-accordion__summary {
  font-size: 13px;
  line-height: 1.4;
  color: var(--ai-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ai-details-accordion__chevron {
  color: #6e6b7b;
  margin-top: 0.15rem;
}
.ai-script-count-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 0.3rem 0.55rem;
  flex-shrink: 0;
}
.min-width-0 {
  min-width: 0;
}

.ai-question-flow {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.ai-question-form {
  max-width: none;
  width: 100%;
  min-width: 0;
  padding: 1.2rem 1.25rem 1.3rem;
  background: #fff;
  overflow: visible;
}
.ai-form-block + .ai-form-block {
  margin-top: 1.15rem;
  padding-top: 1.15rem;
  border-top: 1px solid var(--ai-border);
}
.ai-calling-tab ::v-deep .ai-form-question,
.ai-form-question {
  display: block;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--ai-heading);
  line-height: 1.35;
  margin-bottom: 0.3rem;
}
.ai-calling-tab ::v-deep .ai-form-hint,
.ai-form-hint {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
  margin-bottom: 0.75rem;
}

.ai-section-card {
  border: 1px solid var(--ai-border);
  border-radius: var(--ai-radius);
  background: #fff;
  overflow: hidden;
  box-shadow: none;
}
.ai-question-form.ai-section-card {
  overflow: visible;
}
.ai-section-card--accent {
  border-color: var(--ai-primary-border);
}
.ai-section-card__header {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.85rem 1rem;
  background: var(--ai-surface);
  border-bottom: 1px solid var(--ai-border);
}
.ai-section-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.4rem;
  background: var(--ai-primary-soft);
  color: #007a6e;
  flex-shrink: 0;
}
.ai-section-card__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ai-heading);
  line-height: 1.3;
  margin: 0 0 0.15rem;
}
.ai-section-card__desc {
  font-size: 14px;
  color: var(--ai-muted);
  margin: 0;
  line-height: 1.4;
}
.ai-section-card__body {
  padding: 1rem;
}
.ai-section-card__body--top-border {
  border-top: 1px solid var(--ai-border);
}

.ai-agent-setup {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.65rem;
  padding: 0.45rem 0;
  border-radius: 0.4rem;
  font-size: 13px;
  line-height: 1.4;
}
.ai-agent-setup__icon {
  margin-top: 0.1rem;
}
.ai-agent-setup__body {
  min-width: 0;
  flex: 1 1 auto;
}
.ai-agent-setup--setting_up {
  background: transparent;
  color: var(--ai-muted);
}
.ai-agent-setup--ready {
  background: transparent;
  color: #007a6e;
  font-weight: 600;
}
.ai-agent-setup--error,
.ai-agent-setup--blocked {
  background: #fdeaea;
  color: #ea5455;
  padding: 0.65rem 0.8rem;
}
.ai-agent-setup--blocked {
  font-weight: 600;
}

.ai-question-reveal-enter-active,
.ai-question-reveal-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.ai-question-reveal-enter,
.ai-question-reveal-leave-to {
  opacity: 0;
  transform: translateY(0.4rem);
}

.ai-prompt-job-details {
  margin-top: 0.5rem;
}
.ai-prompt-job-details__body {
  padding: 0.5rem 0 0;
}
.ai-prompt-job-details__toggle {
  color: var(--ai-heading);
  text-decoration: none;
  border: none;
  border-radius: 0;
  padding: 0.5rem 0;
  background: transparent;
  font-size: 15px;
  font-weight: 600;
}
.ai-prompt-job-details__toggle:hover:not(:disabled),
.ai-prompt-job-details__toggle:focus:not(:disabled) {
  color: var(--ai-primary);
  text-decoration: none;
  box-shadow: none;
}
.ai-optional-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 0.2rem 0.45rem;
}

.ai-cta,
.ai-calling-tab ::v-deep .ai-cta {
  min-height: 2.5rem;
  min-width: 10.5rem;
  padding: 0.5rem 1.15rem;
  font-size: 15px;
  font-weight: 600;
  box-shadow: none !important;
}
.ai-cta-secondary,
.ai-calling-tab ::v-deep .ai-cta-secondary {
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  font-size: 15px;
  font-weight: 600;
  box-shadow: none !important;
}

.ai-test-call-card {
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}
.ai-setup-aside .ai-test-call-actions {
  padding-top: 0;
}
.ai-setup-aside .ai-cta {
  width: 100%;
  min-width: 0;
}
.ai-test-call-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem;
}
.ai-test-call-field {
  flex: 1 1 16rem;
  min-width: 0;
}
.ai-test-call-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--ai-heading);
  margin-bottom: 0.35rem;
}
.ai-test-call-help {
  display: block;
  margin-top: 0.35rem;
  font-size: 14px;
  line-height: 1.4;
  color: var(--ai-muted);
}
.ai-test-call-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
@media (min-width: 576px) {
  .ai-test-call-actions {
    padding-top: 1.7rem;
  }
}
.ai-test-call-actions--edit {
  justify-content: flex-end;
}
.ai-next-step-hint {
  margin-top: 0.75rem;
  padding: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  background: transparent;
  color: #5e5873;
}

.ai-approval-gate {
  text-align: center;
  padding: 1.75rem 1.15rem;
}
.ai-approval-gate__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: var(--ai-primary-soft);
  color: var(--ai-primary);
  margin-bottom: 0.85rem;
}
.ai-approval-gate__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--ai-heading);
}
.ai-approval-gate__hint {
  font-size: 15px;
  color: var(--ai-muted);
}

.ai-preview-audio {
  border-radius: 0.4rem;
  height: 2.5rem;
}
.ai-status-line {
  display: flex;
  align-items: center;
  padding: 0.65rem 0.8rem;
  background: var(--ai-surface);
  border-radius: 0.4rem;
  font-size: 14px;
  color: var(--ai-text);
}
.ai-calling-actions__toolbar {
  padding-bottom: 0.75rem;
  margin-bottom: 0.85rem;
  border-bottom: 1px solid var(--ai-border);
}
.ai-calling-campaign {
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px dashed var(--ai-border);
}
.ai-campaign-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ai-heading);
}
.ai-campaign-meta {
  font-size: 14px;
  color: var(--ai-muted);
}
.ai-status-pill {
  font-size: 12px;
  font-weight: 600;
  padding: 0.3em 0.65em;
  border-radius: 999px;
}

.ai-history-section {
  margin-top: 0.85rem;
}
.ai-history-toggle {
  color: inherit;
  text-decoration: none;
  border: none;
  border-radius: 0;
}
.ai-history-toggle:hover,
.ai-history-toggle:focus {
  color: inherit;
  text-decoration: none;
  box-shadow: none;
}
.ai-history-toggle:hover .ai-section-card__title,
.ai-history-toggle:focus .ai-section-card__title {
  color: var(--ai-primary);
}

@media (max-width: 575.98px) {
  .ai-cta,
  .ai-calling-tab ::v-deep .ai-cta {
    width: 100%;
    min-width: 0;
  }
}
</style>
